import './InternalSecondaryMobile.css';
// Service Division Tracker — Service tab in the maintenance hub.
//
// Shows whether each service coordinator's team is hitting its invoicing
// quota for the selected timeframe. Each coordinator manages a team of
// technicians and is responsible for booking jobs and sending invoices.
//
// Data is LIVE from Jobber, with team assignments managed in Back4App
// via the Manage Team modal:
//   /api/jobber/team           — roster, roles, daily quotas
//   /api/jobber/quota-progress — invoiced totals per team/tech for a
//                                date range (salesperson attribution,
//                                falling back to visit assignees)
//   /api/jobber/roster-admin   — Manage Team modal (assign techs to
//                                teams/tiers, projects, or vacation)
//   /api/jobber/tech-skills    — Manage Skills matrix (who can do what)
//
// Styling mirrors MaintenancePnlCalendar / MaintenanceBuildingHeatmap
// (same palette, font, card + shadow language).

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FaCalendarAlt, FaChartLine, FaCheck, FaChevronDown, FaExternalLinkAlt, FaFileInvoiceDollar, FaHome, FaSort, FaSortDown, FaSortUp, FaSyncAlt, FaTimes, FaWrench } from 'react-icons/fa';
import API_BASE_URL from './config/api';
import { cacheGet, cachePut } from './utils/staleCache';
import { jobberDisplayName as displayName } from './utils/jobberNames';

const C = {
  navy: '#1F3A68',
  ink: '#1a2236',
  inkSoft: '#374151',
  muted: '#6b7280',
  line: '#E6E8EC',
  lineSoft: '#EFF2F7',
  bg: '#F4F7FB',
  card: '#ffffff',
  green: '#1c9550',
  greenSoft: '#E3F5EA',
  red: '#B62525',
  redSoft: '#FBE2E2',
  amber: '#b45309',
  amberSoft: '#FEF3C7',
  blue: '#2563eb',
  blueSoft: '#eff6ff',
  shadow: '0 1px 3px rgba(20,30,60,.06), 0 8px 24px rgba(20,30,60,.04)',
};

const sharedFont =
  '-apple-system, BlinkMacSystemFont, "Segoe UI", "Inter", "Helvetica Neue", Arial, sans-serif';

// ── Team data ────────────────────────────────────────────────────
// Roster (coordinators, techs, roles, hourly rates, DAILY quotas)
// comes from /api/jobber/team; quotas scale by the number of workdays
// in the selected timeframe.
const TEAM_META = {
  Ergi: { id: 'ergi', avatarBg: '#22C55E' },
  Christina: { id: 'christina', avatarBg: '#F59E0B' },
};
// New coordinators get colors from this palette, in roster order.
const EXTRA_COLORS = ['#3B82F6', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316', '#0EA5E9'];

const TEAM_CACHE_KEY = 'quotaTrackerTeam';
const VISIBLE_TEAMS_LS_KEY = 'quotaTrackerVisibleTeams';
const PROGRESS_CACHE_PREFIX = 'quotaTrackerProgress:';
const UNSENT_CACHE_PREFIX = 'quotaTrackerUnsent:';
const PERF_CACHE_KEY = 'quotaTrackerPerf';

// "Mechanical Service" -> "Mechanical", "In-Suite Service" -> "In-Suite"
function teamLabel(specialty) {
  return String(specialty || '').replace(/\s*Service\s*$/i, '');
}

// Stable "random" avatar color: the same name always hashes to the
// same palette entry, so it doesn't shuffle on every render/refresh.
const NAME_COLORS = ['#2563EB', '#7C3AED', '#DB2777', '#0D9488', '#EA580C', '#4F46E5', '#0891B2', '#65A30D', '#C026D3', '#B45309'];
function colorForName(name) {
  let h = 0;
  const s = String(name || '');
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return NAME_COLORS[h % NAME_COLORS.length];
}

// Shape the /api/jobber/team response into what the page renders.
// One entry per coordinator: two In-Suite coordinators = two rows,
// each keyed by the coordinator's Jobber id (`key`), which is also
// how the backend buckets progress / unsent / performance data.
function coordinatorsFromTeams(teams) {
  const seenIds = new Set();
  let extraColorIdx = 0;
  return (teams || []).map((t) => {
    const meta = TEAM_META[t.coordinator.name];
    const avatarBg = meta ? meta.avatarBg : EXTRA_COLORS[extraColorIdx++ % EXTRA_COLORS.length];
    let id = (meta && meta.id) || t.coordinator.name.toLowerCase().replace(/[^a-z0-9]+/g, '');
    while (seenIds.has(id)) id += '2';
    seenIds.add(id);
    const label = teamLabel(t.specialty);
    return {
      id,
      key: t.key || t.coordinator.jobberId,
      name: t.coordinator.name,
      teamLabel: label,
      // Row title: "Mechanical (Ergi)" / "In-Suite (Christina)"
      title: `${label} (${t.coordinator.name})`,
      specialty: t.specialty,
      avatarBg,
      techs: (t.techs || []).map((tech) => ({
        id: tech.jobberId,
        name: displayName(tech.name),
        hourlyRate: tech.hourlyRate,
        role: tech.role,
        dailyQuota: tech.dailyQuota || 0,
        status: tech.status || 'active',
      })),
    };
  });
}

// Timeframe definitions:
// - today: today so far
// - tomorrow: the next day — a projection (nothing invoiced yet, but
//   quota and booked jobs show what's lined up)
// - yesterday: the prior day
// - this_week: this week's Monday through today so far; quota is the
//   full work week (Monday–Friday)
// - last_week: the previous full calendar week
const TIMEFRAMES = [
  { key: 'today', label: 'Today' },
  { key: 'tomorrow', label: 'Tomorrow' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'this_week', label: 'This Week' },
  { key: 'last_week', label: 'Last Week' },
  { key: 'last_month', label: 'Last Month' },
  { key: 'last_30', label: 'Last 30 Days' },
  { key: 'last_90', label: 'Last 90 Days' },
  { key: 'alltime', label: 'All Time' },
  { key: 'custom', label: 'Custom' },
];

// Count Mon–Fri days between two dates, inclusive.
function weekdaysBetween(start, end) {
  let count = 0;
  const d = new Date(start);
  while (d <= end) {
    const dow = d.getDay();
    if (dow !== 0 && dow !== 6) count += 1;
    d.setDate(d.getDate() + 1);
  }
  return count;
}

// Quotas are per-workday, so each timeframe's quota = dailyQuota ×
// the number of Mon–Fri days it covers. Saturdays and Sundays never
// contribute — Today/Yesterday/Tomorrow on a weekend has a $0 quota
// (weekend invoices still count toward totals, just against no target).
function workdaysForTimeframe(timeframeKey, customStart, customEnd) {
  const range = rangeForTimeframe(timeframeKey, customStart, customEnd);
  if (!range) return 0;
  return weekdaysBetween(
    new Date(range.start + 'T00:00:00'),
    new Date(range.end + 'T00:00:00')
  );
}

function toISODate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// When the company started using Jobber — "All Time" starts here.
const JOBBER_EPOCH = '2026-01-01';

// Date range (local) the backend should aggregate invoices over.
// Returns undefined while a custom range is incomplete.
function rangeForTimeframe(timeframeKey, customStart, customEnd) {
  const now = new Date();
  if (timeframeKey === 'today') {
    const d = toISODate(now);
    return { start: d, end: d };
  }
  if (timeframeKey === 'tomorrow') {
    const t = new Date(now);
    t.setDate(t.getDate() + 1);
    const d = toISODate(t);
    return { start: d, end: d };
  }
  if (timeframeKey === 'yesterday') {
    const y = new Date(now);
    y.setDate(y.getDate() - 1);
    const d = toISODate(y);
    return { start: d, end: d };
  }
  if (timeframeKey === 'this_week') {
    const daysSinceMonday = (now.getDay() + 6) % 7;
    const monday = new Date(now);
    monday.setDate(now.getDate() - daysSinceMonday);
    const friday = new Date(monday);
    friday.setDate(monday.getDate() + 4);
    return { start: toISODate(monday), end: toISODate(friday) };
  }
  if (timeframeKey === 'last_week') {
    const daysSinceMonday = (now.getDay() + 6) % 7;
    const lastMonday = new Date(now);
    lastMonday.setDate(now.getDate() - daysSinceMonday - 7);
    const lastSunday = new Date(lastMonday);
    lastSunday.setDate(lastMonday.getDate() + 6);
    return { start: toISODate(lastMonday), end: toISODate(lastSunday) };
  }
  if (timeframeKey === 'last_month') {
    const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const last = new Date(now.getFullYear(), now.getMonth(), 0);
    return { start: toISODate(first), end: toISODate(last) };
  }
  if (timeframeKey === 'last_30' || timeframeKey === 'last_90') {
    const days = timeframeKey === 'last_30' ? 30 : 90;
    const start = new Date(now);
    start.setDate(start.getDate() - (days - 1));
    return { start: toISODate(start), end: toISODate(now) };
  }
  if (timeframeKey === 'alltime') {
    return { start: JOBBER_EPOCH, end: toISODate(now) };
  }
  if (timeframeKey === 'custom') {
    if (customStart && customEnd) return { start: customStart, end: customEnd };
    return undefined;
  }
  return null;
}

// ── Long-range fetching ──────────────────────────────────────────
// Heroku cuts requests at 30s, and a single 90-day / all-time invoice
// scan can take longer (Jobber pages + throttle backoff). Ranges longer
// than ~6 weeks are fetched one calendar month at a time — each chunk
// is fast, gets cached server-side, and the pieces are merged here.

const CHUNK_THRESHOLD_DAYS = 45;

function spanDays(range) {
  return (new Date(range.end + 'T00:00:00') - new Date(range.start + 'T00:00:00')) / 86400000;
}

function monthChunks(startIso, endIso) {
  const chunks = [];
  let cur = new Date(startIso + 'T00:00:00');
  const end = new Date(endIso + 'T00:00:00');
  while (cur <= end) {
    const monthEnd = new Date(cur.getFullYear(), cur.getMonth() + 1, 0);
    const chunkEnd = monthEnd < end ? monthEnd : end;
    chunks.push({ start: toISODate(cur), end: toISODate(chunkEnd) });
    cur = new Date(cur.getFullYear(), cur.getMonth() + 1, 1);
  }
  return chunks;
}

// Merge month-chunk /quota-progress responses. Safe to sum: invoices
// are bucketed by work date, so each one lands in exactly one chunk.
function mergeProgress(parts) {
  const out = {
    start: parts[0].start,
    end: parts[parts.length - 1].end,
    teams: {},
    targets: {},
    unassigned: { invoiced: 0, count: 0, invoices: [] },
    allInvoices: [],
    totalInvoiced: 0,
    invoiceCount: 0,
  };
  for (const p of parts) {
    out.totalInvoiced += p.totalInvoiced || 0;
    out.invoiceCount += p.invoiceCount || 0;
    out.unassigned.invoiced += (p.unassigned && p.unassigned.invoiced) || 0;
    out.unassigned.count += (p.unassigned && p.unassigned.count) || 0;
    out.unassigned.invoices.push(...((p.unassigned && p.unassigned.invoices) || []));
    out.allInvoices.push(...(p.allInvoices || []));
    for (const [key, tgt] of Object.entries(p.targets || {})) {
      const g = out.targets[key] || (out.targets[key] = { total: 0, techs: {} });
      g.total += tgt.total || 0;
      for (const [tid, info] of Object.entries(tgt.techs || {})) {
        const x = g.techs[tid] || (g.techs[tid] = { name: info.name, target: 0 });
        x.target += info.target || 0;
      }
    }
    for (const [key, team] of Object.entries(p.teams || {})) {
      const t = out.teams[key] || (out.teams[key] = { invoiced: 0, count: 0, techs: {} });
      t.invoiced += team.invoiced || 0;
      t.count += team.count || 0;
      for (const [techId, tech] of Object.entries(team.techs || {})) {
        const x = t.techs[techId] || (t.techs[techId] = { invoiced: 0, count: 0, invoices: [] });
        x.invoiced += tech.invoiced || 0;
        x.count += tech.count || 0;
        x.invoices.push(...(tech.invoices || []));
      }
    }
  }
  return out;
}

// Merge month-chunk /unsent-invoices responses. A job whose visits span
// two months would appear in both chunks, so dedupe by job and rebuild
// the totals from the deduped lists.
function mergeUnsent(parts) {
  const out = {
    start: parts[0].start,
    end: parts[parts.length - 1].end,
    teams: {},
    unassigned: { total: 0, count: 0, jobs: [] },
  };
  const buckets = [];
  for (const p of parts) {
    for (const [key, team] of Object.entries(p.teams || {})) {
      let b = out.teams[key];
      if (!b) { b = out.teams[key] = { total: 0, count: 0, jobs: [], seen: new Set() }; buckets.push(b); }
      for (const j of team.jobs || []) {
        const id = `${j.jobNumber}-${j.url}`;
        if (!b.seen.has(id)) { b.seen.add(id); b.jobs.push(j); }
      }
    }
    const u = out.unassigned;
    if (!u.seen) { u.seen = new Set(); buckets.push(u); }
    for (const j of (p.unassigned && p.unassigned.jobs) || []) {
      const id = `${j.jobNumber}-${j.url}`;
      if (!u.seen.has(id)) { u.seen.add(id); u.jobs.push(j); }
    }
  }
  for (const b of buckets) {
    delete b.seen;
    b.jobs.sort((a, z) => String(z.endAt || '').localeCompare(String(a.endAt || '')));
    b.total = b.jobs.reduce((s, j) => s + (j.amount || 0), 0);
    b.count = b.jobs.length;
  }

  // Rebuild the booked totals from the flat job lists: a job with
  // visits in two months shows up in both chunks and must count once.
  const bookedSeen = new Set();
  const bookedTeams = {};
  const bookedUnassigned = { total: 0, count: 0 };
  const bookedTechs = {};
  const bookedJobs = [];
  for (const p of parts) {
    for (const j of p.bookedJobs || []) {
      if (bookedSeen.has(j.id)) continue;
      bookedSeen.add(j.id);
      bookedJobs.push(j);
      const tgt = j.team
        ? (bookedTeams[j.team] = bookedTeams[j.team] || { total: 0, count: 0 })
        : bookedUnassigned;
      tgt.total += j.amount || 0;
      tgt.count += 1;
      const techIds = j.techs || [];
      if (techIds.length) {
        const share = (j.amount || 0) / techIds.length;
        for (const id of techIds) bookedTechs[id] = (bookedTechs[id] || 0) + share;
      }
    }
  }
  out.booked = { teams: bookedTeams, unassigned: bookedUnassigned, techs: bookedTechs };
  out.bookedJobs = bookedJobs;
  return out;
}

// Merge the roster with real invoiced totals from /quota-progress.
// `progress` may be null while loading — rows still render with $0.
// Targets come from the backend's roster history when available, so a
// past range shows the quota the team actually had on those days;
// "current roster × workdays" is only the fallback while loading.
function buildRows(coordinators, progress, timeframeKey, customStart, customEnd) {
  const workdays = workdaysForTimeframe(timeframeKey, customStart, customEnd);

  return coordinators.map((c) => {
    const teamAgg = progress ? progress.teams[c.key] : null;
    const targets = progress && progress.targets ? progress.targets[c.key] : null;

    const techRows = c.techs.map((t) => {
      const tQuota = targets
        ? ((targets.techs[t.id] || {}).target || 0)
        : t.dailyQuota * workdays;
      const tAgg = teamAgg && teamAgg.techs ? teamAgg.techs[t.id] : null;
      return {
        ...t,
        quota: tQuota,
        invoiced: tAgg ? tAgg.invoiced : 0,
        invoices: tAgg && tAgg.invoices ? tAgg.invoices : [],
      };
    });

    // Techs who were on this team during the range but aren't on the
    // current roster (moved, projects, departed) — their old target
    // and invoices still belong to this range.
    if (targets) {
      for (const [id, info] of Object.entries(targets.techs)) {
        if (c.techs.some((t) => t.id === id)) continue;
        const tAgg = teamAgg && teamAgg.techs ? teamAgg.techs[id] : null;
        if (!(info.target > 0) && !(tAgg && tAgg.invoiced > 0)) continue;
        techRows.push({
          id,
          name: displayName(info.name || '—'),
          role: 'No longer on team',
          dailyQuota: 0,
          status: 'active',
          quota: info.target || 0,
          invoiced: tAgg ? tAgg.invoiced : 0,
          invoices: tAgg && tAgg.invoices ? tAgg.invoices : [],
        });
      }
    }

    const quota = techRows.reduce((s, t) => s + t.quota, 0);
    // Team total comes from the backend (it can exceed the tech sum
    // when invoices attribute to the team but not a specific tech).
    const invoiced = teamAgg ? teamAgg.invoiced : 0;

    return { ...c, quota, invoiced, techRows };
  });
}

// ── Formatting helpers ───────────────────────────────────────────
function fmtMoney(n) {
  return '$' + Math.round(n).toLocaleString('en-US');
}

function fmtDate(d, withYear = true) {
  return d.toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', ...(withYear ? { year: 'numeric' } : {}),
  });
}

// Actual calendar dates covered by a timeframe, for the header label.
// Derived from the same range sent to the backend, so the label always
// matches the data shown. Null for all-time / incomplete custom range.
function dateRangeLabel(timeframeKey, customStart, customEnd) {
  const range = rangeForTimeframe(timeframeKey, customStart, customEnd);
  if (!range) return null;
  const s = new Date(range.start + 'T00:00:00');
  const e = new Date(range.end + 'T00:00:00');
  if (range.start === range.end) return fmtDate(s);
  return `${fmtDate(s, false)} – ${fmtDate(e)}`;
}

function pctColors(pct) {
  if (pct >= 90) return { bg: C.greenSoft, fg: C.green };
  if (pct >= 70) return { bg: C.amberSoft, fg: C.amber };
  return { bg: C.redSoft, fg: C.red };
}

// ── Small components ─────────────────────────────────────────────
// Team avatar: a wrench for Mechanical, a home for In-Suite.
function TeamAvatar({ specialty, bg }) {
  const isMech = /mechanical/i.test(specialty || '');
  const Icon = isMech ? FaWrench : FaHome;
  return (
    <div style={{
      width: 44, height: 44, borderRadius: '50%', background: bg, color: '#fff',
      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    }}>
      <Icon style={{ fontSize: 18 }} />
    </div>
  );
}

// Custom dropdown so the trigger and options can be styled bigger than
// a native <select> allows.
// Compact date hint shown under each dropdown option, e.g. "Aug 3"
// or "Jul 28 – Aug 1". Null when there's nothing meaningful to show
// (all-time, or a custom range that isn't fully picked yet).
function optionDates(timeframeKey, customStart, customEnd) {
  const r = rangeForTimeframe(timeframeKey, customStart, customEnd);
  if (!r) return null;
  const s = new Date(r.start + 'T00:00:00');
  const e = new Date(r.end + 'T00:00:00');
  if (r.start === r.end) return fmtDate(s, false);
  return `${fmtDate(s, false)} – ${fmtDate(e, false)}`;
}

function TimeframeDropdown({ value, onChange, customStart, customEnd }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const current = TIMEFRAMES.find((t) => t.key === value) || TIMEFRAMES[0];

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
          width: 220, fontFamily: sharedFont, fontSize: 15, fontWeight: 700, color: C.ink,
          background: C.card, border: `1px solid ${open ? C.navy : C.line}`, borderRadius: 12,
          padding: '12px 16px', cursor: 'pointer', boxShadow: C.shadow, outline: 'none',
        }}
      >
        {current.label}
        <FaChevronDown style={{
          fontSize: 13, color: C.muted, transition: 'transform .2s ease',
          transform: open ? 'rotate(180deg)' : 'none',
        }} />
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 50,
          width: 220, background: C.card, border: `1px solid ${C.line}`, borderRadius: 12,
          boxShadow: '0 4px 10px rgba(20,30,60,.10), 0 16px 40px rgba(20,30,60,.12)',
          padding: 6, overflow: 'hidden',
        }}>
          {TIMEFRAMES.map((t) => {
            const active = t.key === value;
            const sub = optionDates(t.key, customStart, customEnd);
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => { onChange(t.key); setOpen(false); }}
                onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = C.lineSoft; }}
                onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = 'transparent'; }}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  width: '100%', fontFamily: sharedFont, fontSize: 15,
                  fontWeight: active ? 800 : 600, color: active ? C.navy : C.inkSoft,
                  background: active ? C.blueSoft : 'transparent',
                  border: 'none', borderRadius: 8, padding: '10px 14px',
                  cursor: 'pointer', textAlign: 'left',
                }}
              >
                <span>
                  {t.label}
                  {sub && (
                    <span style={{
                      display: 'block', fontSize: 12, fontWeight: 500,
                      color: C.muted, marginTop: 2,
                    }}>
                      {sub}
                    </span>
                  )}
                </span>
                {active && <FaCheck style={{ fontSize: 12, color: C.navy, flexShrink: 0 }} />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function PctBadge({ pct, small }) {
  const badge = pctColors(pct);
  return (
    <span style={{
      display: 'inline-block', minWidth: small ? 48 : 56, textAlign: 'center',
      fontWeight: 800, fontSize: small ? 12.5 : 14, color: badge.fg, background: badge.bg,
      padding: small ? '5px 10px' : '7px 12px', borderRadius: 9, whiteSpace: 'nowrap',
    }}>
      {Math.round(pct)}%{pct >= 100 ? ' 🎉' : ''}
    </span>
  );
}

// ── Face avatars (experimental) ──────────────────────────────────
// Set SHOW_FACE_AVATARS to false to fully revert to the initial
// circles / no face beside the team bars. Jobber's API doesn't expose
// profile photos, so fill FACE_PHOTOS with name -> image URL entries;
// anyone missing falls back to the Hudson logo.
const SHOW_FACE_AVATARS = false;
const FACE_PHOTOS = {
  // 'ergi xhafa': 'https://…/ergi.jpg',
};
const FACE_PLACEHOLDER = `${process.env.PUBLIC_URL || ''}/logo192.png`;
const facePhotoFor = (name) => FACE_PHOTOS[(name || '').trim().toLowerCase()] || FACE_PLACEHOLDER;

function FaceAvatar({ name, size = 28, ring }) {
  return (
    <img
      src={facePhotoFor(name)}
      alt={name}
      title={name}
      loading="lazy"
      decoding="async"
      style={{
        width: size, height: size, borderRadius: '50%', objectFit: 'cover',
        border: `2px solid ${ring || '#fff'}`,
        boxShadow: '0 1px 3px rgba(16,38,73,.18)',
        background: '#fff', flexShrink: 0,
      }}
    />
  );
}

function TechRow({ tech, color, booked, onOpenInvoices }) {
  const pct = tech.quota > 0 ? (tech.invoiced / tech.quota) * 100 : 0;
  const remaining = Math.max(0, tech.quota - tech.invoiced);
  const fillPct = Math.min(100, pct);
  const showRemaining = remaining > 0 && 100 - fillPct >= 8;

  // Vacation: still on the team, target paused. Only when the range
  // has no target for them — on a historical range where they were
  // active, their real quota bar shows instead.
  if (tech.status === 'vacation' && !(tech.quota > 0)) {
    return (
      <div className="quota-mobile-row"
        onClick={() => onOpenInvoices(tech)}
        title="View this tech's invoices"
        onMouseEnter={(e) => { e.currentTarget.style.background = '#DCE4EE'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
        style={{
          display: 'grid',
          gridTemplateColumns: '200px 1fr 110px 86px',
          alignItems: 'center',
          gap: 18,
          padding: '13px 22px',
          borderBottom: '1px solid #D9E0EA',
          cursor: 'pointer',
          opacity: 0.72,
        }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingLeft: 44, minWidth: 0 }}>
          {SHOW_FACE_AVATARS ? (
            <FaceAvatar name={tech.name} size={26} ring="#9CA3AF" />
          ) : (
            <div style={{
              width: 26, height: 26, borderRadius: '50%', background: '#9CA3AF', color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontWeight: 700, fontSize: 12, flexShrink: 0,
            }}>
              {tech.name[0].toUpperCase()}
            </div>
          )}
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: 13.5, color: C.inkSoft, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {tech.name}
            </div>
            {tech.role && (
              <div style={{ fontSize: 10.5, color: C.muted, whiteSpace: 'nowrap' }}>{tech.role}</div>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{
            fontSize: 11, fontWeight: 700, color: C.muted, background: '#E7EBF2',
            padding: '4px 10px', borderRadius: 999, whiteSpace: 'nowrap',
          }}>
            🏖 On vacation
          </span>
          {tech.invoiced > 0 && (
            <span style={{ fontSize: 11.5, fontWeight: 700, color, whiteSpace: 'nowrap' }}>
              {fmtMoney(tech.invoiced)} invoiced
            </span>
          )}
        </div>

        <div style={{ textAlign: 'right' }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: C.muted, whiteSpace: 'nowrap' }}>
            —{' '}
            <span style={{ fontSize: 10.5, fontWeight: 400 }}>Paused</span>
          </div>
          {booked > 0 && (
            <div
              title="Value of jobs booked for this tech in this timeframe, invoiced or not"
              style={{ fontSize: 10.5, fontWeight: 700, color: '#15803D', marginTop: 2, whiteSpace: 'nowrap' }}
            >
              {fmtMoney(booked)}{' '}
              <span style={{ fontSize: 9.5, fontWeight: 400 }}>Booked</span>
            </div>
          )}
        </div>

        <div style={{ justifySelf: 'end', fontSize: 12.5, fontWeight: 700, color: C.muted }}>—</div>
      </div>
    );
  }

  return (
    <div className="quota-mobile-row"
      onClick={() => onOpenInvoices(tech)}
      title="View this tech's invoices"
      onMouseEnter={(e) => { e.currentTarget.style.background = '#DCE4EE'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
      style={{
        display: 'grid',
        gridTemplateColumns: '200px 1fr 110px 86px',
        alignItems: 'center',
        gap: 18,
        padding: '13px 22px',
        borderBottom: '1px solid #D9E0EA',
        cursor: 'pointer',
      }}>
      {/* Tech avatar + name, indented to sit under the coordinator's name. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingLeft: 44, minWidth: 0 }}>
        {SHOW_FACE_AVATARS ? (
          <FaceAvatar name={tech.name} size={28} ring={color} />
        ) : (
          <div style={{
            width: 28, height: 28, borderRadius: '50%', background: color, color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 700, fontSize: 13, flexShrink: 0,
          }}>
            {tech.name[0].toUpperCase()}
          </div>
        )}
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 14.5, color: C.inkSoft, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {tech.name}
          </div>
          {tech.role && (
            <div style={{ fontSize: 11.5, color: C.muted, whiteSpace: 'nowrap' }}>{tech.role}</div>
          )}
        </div>
      </div>

      {/* Mini bar */}
      <div style={{ minWidth: 0 }}>
        <div style={{
          position: 'relative', height: 20, borderRadius: 10,
          background: '#E7EBF2', overflow: 'hidden',
        }}>
          <div style={{
            position: 'absolute', inset: 0, width: `${fillPct}%`,
            background: `${color}D9`,
            borderRadius: 9,
            transition: 'width .45s ease',
          }} />
          {showRemaining && (
            <div style={{
              position: 'absolute', top: 0, bottom: 0, left: `${fillPct}%`, right: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'left .45s ease',
            }}>
              <span style={{ fontSize: 11.5, fontWeight: 700, color: C.muted, whiteSpace: 'nowrap' }}>
                {fmtMoney(remaining)}
              </span>
            </div>
          )}
        </div>
        <div style={{
          width: `${fillPct}%`, minWidth: 60, textAlign: 'right',
          marginTop: 3, transition: 'width .45s ease',
        }}>
          <span style={{ fontWeight: 700, fontSize: 12.5, color, whiteSpace: 'nowrap' }}>
            {fmtMoney(tech.invoiced)}{' '}
            <span style={{ fontSize: 10.5, fontWeight: 400 }}>Invoiced</span>
          </span>
        </div>
      </div>

      {/* Tech quota */}
      <div style={{ textAlign: 'right' }}>
        <div style={{ fontWeight: 700, fontSize: 14, color: C.inkSoft, whiteSpace: 'nowrap' }}>
          {fmtMoney(tech.quota)}{' '}
          <span style={{ fontSize: 11.5, fontWeight: 400, color: C.muted }}>Quota</span>
        </div>
        {booked > 0 && (
          <div
            title="Value of jobs booked for this tech in this timeframe, invoiced or not"
            style={{ fontSize: 11.5, fontWeight: 700, color: '#15803D', marginTop: 2, whiteSpace: 'nowrap' }}
          >
            {fmtMoney(booked)}{' '}
            <span style={{ fontSize: 10.5, fontWeight: 400 }}>Booked</span>
          </div>
        )}
      </div>

      <div style={{ justifySelf: 'end' }}>
        <PctBadge pct={pct} small />
      </div>
    </div>
  );
}

function QuotaRow({ row, teamInvoices, booked, bookedTechs, expanded, onToggle, onOpenTechInvoices }) {
  const pct = row.quota > 0 ? (row.invoiced / row.quota) * 100 : 0;
  const remaining = Math.max(0, row.quota - row.invoiced);

  // Every bar track spans the full column width; the fill is relative
  // to that coordinator's own quota and uses their avatar color.
  const fillPct = Math.min(100, pct);
  const showRemaining = remaining > 0 && 100 - fillPct >= 8;

  return (
    <div style={{ borderBottom: `1px solid ${C.lineSoft}` }}>
      <div className="quota-mobile-row"
        onClick={onToggle}
        style={{
          display: 'grid',
          gridTemplateColumns: '200px 1fr 110px 86px',
          alignItems: 'center',
          gap: 18,
          padding: '18px 22px',
          cursor: 'pointer',
          background: expanded ? '#FAFBFD' : 'transparent',
        }}
      >
        {/* Coordinator + accordion arrow */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
          <FaChevronDown style={{
            fontSize: 12, color: C.muted, flexShrink: 0,
            transition: 'transform .2s ease',
            transform: expanded ? 'rotate(180deg)' : 'none',
          }} />
          <TeamAvatar specialty={row.specialty} bg={row.avatarBg} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 800, fontSize: 15.5, color: C.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {row.teamLabel || row.name}
            </div>
            <div style={{ fontSize: 14, fontWeight: 600, color: C.inkSoft, marginTop: 3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              Lead: {row.name}
            </div>
            <div style={{ marginTop: 3 }}>
              <span style={{
                display: 'inline-block', fontSize: 12.5, fontWeight: 700, color: C.navy,
                background: '#DBEAFE', borderRadius: 999, padding: '2px 10px',
              }}>
                {row.techRows.length} techs
              </span>
            </div>
          </div>
        </div>

        {/* Bar + earnings label under the fill's right edge */}
        <div style={{ minWidth: 0 }}>
          <div style={{
            position: 'relative', height: 28, borderRadius: 14,
            background: C.lineSoft, overflow: 'hidden',
          }}>
            <div style={{
              position: 'absolute', inset: 0, width: `${fillPct}%`,
              background: `linear-gradient(90deg, ${row.avatarBg}, ${row.avatarBg}CC)`,
              borderRadius: 14,
              transition: 'width .45s ease',
            }} />
            {showRemaining && (
              <div style={{
                position: 'absolute', top: 0, bottom: 0, left: `${fillPct}%`, right: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'left .45s ease',
              }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: C.muted, whiteSpace: 'nowrap' }}>
                  {fmtMoney(remaining)}
                </span>
              </div>
            )}
          </div>
          <div style={{
            width: `${fillPct}%`, minWidth: 70, textAlign: 'right',
            marginTop: 5, transition: 'width .45s ease',
          }}>
          <span style={{ fontWeight: 700, fontSize: 13.5, color: row.avatarBg, whiteSpace: 'nowrap' }}>
            {fmtMoney(row.invoiced)}{' '}
            <span style={{ fontSize: 10.5, fontWeight: 400 }}>Invoiced</span>
          </span>
          </div>
        </div>

        {/* Quota total */}
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontWeight: 800, fontSize: 15, color: C.ink, whiteSpace: 'nowrap' }}>
            {fmtMoney(row.quota)}
          </div>
          <div style={{ fontSize: 11.5, color: C.muted, marginTop: 2 }}>Quota total</div>
          {booked != null && (
            <div
              title="Total value of jobs scheduled in this timeframe, invoiced or not"
              style={{ fontSize: 11, color: C.muted, marginTop: 3, whiteSpace: 'nowrap' }}
            >
              Booked{' '}
              <span style={{ fontWeight: 700, color: '#15803D' }}>{fmtMoney(booked)}</span>
            </div>
          )}
        </div>

        {/* Percent badge */}
        <div style={{ justifySelf: 'end' }}>
          <PctBadge pct={pct} />
        </div>
      </div>

      {/* Technician accordion */}
      {expanded && (
        <div style={{ background: '#F1F4F9', borderTop: `1px solid ${C.line}` }}>
          {row.techRows.map((tech) => (
            <TechRow
              key={tech.id}
              tech={tech}
              color={row.avatarBg}
              booked={bookedTechs ? bookedTechs[tech.id] : null}
              onOpenInvoices={(t) => onOpenTechInvoices(t, row.avatarBg)}
            />
          ))}
          <TeamInvoicesSection invoices={teamInvoices} />
        </div>
      )}
    </div>
  );
}

function fmtShortDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function CaseBadge({ caseKey }) {
  const styles = {
    not_marked_complete: { fg: C.amber, bg: C.amberSoft, label: 'Not marked complete' },
    invoice_not_sent: { fg: C.blue, bg: C.blueSoft, label: 'Completed — not invoiced' },
    // Tech finished the work but the job was never closed in Jobber.
    needs_closing: { fg: C.red, bg: C.redSoft, label: 'Needs closing & invoicing' },
    // A completed visit with billable line items on a job that still
    // has future visits (e.g. maintenance plans) — invoiceable now.
    visit_uninvoiced: { fg: '#7C3AED', bg: '#F3EFFD', label: 'Visit done — job ongoing' },
  };
  const s = styles[caseKey] || styles.invoice_not_sent;
  return (
    <span style={{
      display: 'inline-block', fontSize: 10.5, fontWeight: 700, whiteSpace: 'nowrap',
      color: s.fg, background: s.bg,
      padding: '3px 8px', borderRadius: 999,
    }}>
      {s.label}
    </span>
  );
}

// Job rows inside an expanded Unsent Invoices team.
function UnsentJobsList({ jobs, indent = 24 }) {
  const [sort, setSort] = useState({ key: 'date', dir: 'desc' });
  if (!jobs.length) {
    return (
      <div style={{ padding: `14px 22px 14px ${indent}px`, fontSize: 12.5, color: C.muted }}>
        Nothing outstanding in this timeframe.
      </div>
    );
  }
  const onSort = (k) => setSort((s) => toggleSort(s, k));
  const rows = sortedBy(jobs, sort, (j) => j.endAt, (j) => j.amount, {
    sales: (j) => j.salesperson,
    tech: (j) => displayName((j.techs || [])[0] || ''),
    status: (j) => j.case,
  });
  const grid = 'minmax(115px, 1fr) 82px 94px 150px 84px 80px 14px';
  return (
    // The columns need ~700px; inside the half-width card the list
    // scrolls sideways instead of clipping / collapsing columns.
    <div className="internal-data-scroll" role="region" aria-label="Scrollable data table" tabIndex={0} style={{ overflowX: 'auto' }}>
    <div style={{ minWidth: 700 }}>
      <div style={{
        display: 'grid', gridTemplateColumns: grid,
        alignItems: 'center', gap: 8, padding: `8px 18px 7px ${indent}px`,
        borderBottom: '1px solid #D9E0EA',
      }}>
        <span style={colHeadStyle}>Client / Job</span>
        <SortableCol label="Salesperson" colKey="sales" sort={sort} onSort={onSort} />
        <SortableCol label="Technician" colKey="tech" sort={sort} onSort={onSort} />
        <SortableCol label="Status" colKey="status" sort={sort} onSort={onSort} />
        <SortableCol label="Date" colKey="date" sort={sort} onSort={onSort} />
        <SortableCol label="Amount" colKey="amount" sort={sort} onSort={onSort} align="right" />
        <span />
      </div>
      {rows.map((j) => (
        <a
          key={`${j.jobNumber}-${j.url}`}
          href={j.url || '#'}
          target="_blank"
          rel="noreferrer"
          title="Open job in Jobber (new tab)"
          style={{
            display: 'grid',
            gridTemplateColumns: grid,
            alignItems: 'center',
            gap: 8,
            padding: `14px 18px 14px ${indent}px`,
            borderBottom: '1px solid #D9E0EA',
            textDecoration: 'none',
            color: 'inherit',
            cursor: 'pointer',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = '#EDF1F7'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
        >
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: C.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {j.client || '—'}
            </div>
            <div style={{ fontSize: 12.5, color: C.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginTop: 2 }}>
              Job #{j.jobNumber}{j.title ? ` · ${j.title}` : ''}
            </div>
          </div>
          {j.salesperson ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
              <div style={{
                width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
                background: colorForName(displayName(j.salesperson)),
                color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 700, fontSize: 10.5,
              }}>
                {displayName(j.salesperson)[0] ? displayName(j.salesperson)[0].toUpperCase() : '?'}
              </div>
              <span style={{ fontSize: 13, fontWeight: 600, color: C.inkSoft, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {displayName(j.salesperson).split(' ')[0]}
              </span>
            </div>
          ) : (
            <div style={{ fontSize: 13, color: C.muted, fontStyle: 'italic' }}>None</div>
          )}
          {j.techs && j.techs.length ? (
            <div
              title={j.techs.map(displayName).join(', ')}
              style={{ fontSize: 13.5, fontWeight: 600, color: C.inkSoft, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
              {j.techs.slice(0, 2).map((t) => displayName(t).split(' ')[0]).join(', ')}
              {j.techs.length > 2 && ` +${j.techs.length - 2}`}
            </div>
          ) : (
            <div style={{ fontSize: 13.5, color: C.muted, fontStyle: 'italic' }}>None</div>
          )}
          <CaseBadge caseKey={j.case} />
          <div style={{ fontSize: 13.5, color: C.inkSoft, whiteSpace: 'nowrap' }}>{fmtShortDate(j.endAt)}</div>
          <div style={{ fontSize: 14.5, fontWeight: 700, color: C.ink, textAlign: 'right', whiteSpace: 'nowrap' }}>
            {fmtMoney(j.amount)}
          </div>
          <FaExternalLinkAlt style={{ fontSize: 11, color: C.muted }} />
        </a>
      ))}
    </div>
    </div>
  );
}

// Modal listing every invoice credited to one technician. Rows open
// the invoice in Jobber in a new tab.
function TechInvoicesModal({ tech, color, bookedJobs, onClose }) {
  const [tab, setTab] = useState('invoices');
  if (!tech) return null;
  const invoices = tech.invoices || [];
  const booked = bookedJobs || [];
  const bookedTotal = booked.reduce((s, j) => s + (j.share || 0), 0);

  const tabStyle = (active) => ({
    padding: '11px 18px', fontSize: 14, fontWeight: 700, cursor: 'pointer',
    border: 'none', background: 'transparent',
    color: active ? color : C.muted,
    borderBottom: active ? `2.5px solid ${color}` : '2.5px solid transparent',
    marginBottom: -1,
  });

  return (
    <div
      className="secondary-mobile-overlay"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        background: 'rgba(15,23,42,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: C.card, borderRadius: 14, boxShadow: '0 24px 64px rgba(15,23,42,.35)',
          width: 'min(680px, 100%)', maxHeight: '75vh', display: 'flex', flexDirection: 'column',
          overflow: 'hidden', fontFamily: sharedFont,
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '16px 20px', borderBottom: `1px solid ${C.line}`, background: '#FAFBFD',
        }}>
          <div style={{
            width: 36, height: 36, borderRadius: '50%', background: color, color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 15,
          }}>
            {tech.name[0].toUpperCase()}
          </div>
          <div>
            <div style={{ fontWeight: 800, fontSize: 17, color: C.ink }}>{tech.name}</div>
            <div style={{ fontSize: 13.5, color: C.muted, marginTop: 2 }}>
              {invoices.length} invoice{invoices.length === 1 ? '' : 's'} · {fmtMoney(tech.invoiced)} credited
              {' · '}
              <span style={{ color: '#15803D', fontWeight: 700 }}>{fmtMoney(bookedTotal)}</span> booked this timeframe
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              marginLeft: 'auto', width: 34, height: 34, borderRadius: 9,
              background: C.lineSoft, border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.inkSoft,
            }}
          >
            <FaTimes />
          </button>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 4, padding: '0 12px', borderBottom: `1px solid ${C.line}`, background: '#FAFBFD' }}>
          <button type="button" style={tabStyle(tab === 'invoices')} onClick={() => setTab('invoices')}>
            Invoices ({invoices.length})
          </button>
          <button type="button" style={tabStyle(tab === 'booked')} onClick={() => setTab('booked')}>
            Booked jobs ({booked.length})
          </button>
        </div>

        {/* Invoice list */}
        {tab === 'invoices' && (
        <div style={{ overflowY: 'auto' }}>
          {!invoices.length && (
            <div style={{ padding: '36px 24px', textAlign: 'center', fontSize: 13.5, color: C.muted }}>
              No invoices credited to {tech.name} in this timeframe.
            </div>
          )}
          {invoices.map((inv) => (
            <a
              key={`${inv.invoiceNumber}-${inv.url}`}
              href={inv.url || '#'}
              target="_blank"
              rel="noreferrer"
              title="Open invoice in Jobber (new tab)"
              className="quota-invoice-mobile-row"
              style={{
                display: 'grid',
                gridTemplateColumns: '16px 1fr 110px 110px 14px',
                alignItems: 'center',
                gap: 14,
                padding: '14px 20px',
                borderBottom: '1px solid #D9E0EA',
                textDecoration: 'none',
                color: 'inherit',
                cursor: 'pointer',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#F4F7FB'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              <FaFileInvoiceDollar style={{ fontSize: 15, color: C.muted }} />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 14.5, fontWeight: 600, color: C.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {inv.client || '—'}
                </div>
                <div style={{ fontSize: 12.5, color: C.muted, marginTop: 2 }}>
                  Invoice #{inv.invoiceNumber}
                  {inv.split > 1 && ` · split ${inv.split} ways (${fmtMoney(inv.total)} total)`}
                </div>
              </div>
              <div style={{ fontSize: 13.5, color: C.inkSoft, whiteSpace: 'nowrap' }} title="Date the work was completed">
                {fmtShortDate(inv.workDate || inv.issuedDate)}
              </div>
              <div style={{ fontSize: 14.5, fontWeight: 700, color: C.ink, textAlign: 'right', whiteSpace: 'nowrap' }}>
                {fmtMoney(inv.share != null ? inv.share : inv.total)}
              </div>
              <FaExternalLinkAlt style={{ fontSize: 11, color: C.muted }} />
            </a>
          ))}
        </div>
        )}

        {/* Booked jobs list — everything scheduled in the window for
            this tech, invoiced or not */}
        {tab === 'booked' && (
        <div style={{ overflowY: 'auto' }}>
          {!booked.length && (
            <div style={{ padding: '36px 24px', textAlign: 'center', fontSize: 13.5, color: C.muted }}>
              No jobs booked for {tech.name} in this timeframe.
            </div>
          )}
          {booked.map((j) => (
            <a
              key={j.id}
              href={j.url || '#'}
              target="_blank"
              rel="noreferrer"
              title="Open job in Jobber (new tab)"
              className="quota-invoice-mobile-row"
              style={{
                display: 'grid',
                gridTemplateColumns: '16px 1fr 110px 110px 14px',
                alignItems: 'center',
                gap: 14,
                padding: '14px 20px',
                borderBottom: '1px solid #D9E0EA',
                textDecoration: 'none',
                color: 'inherit',
                cursor: 'pointer',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#F4F7FB'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              <FaCalendarAlt style={{ fontSize: 14, color: '#15803D' }} />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 14.5, fontWeight: 600, color: C.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {j.client || '—'}
                </div>
                <div style={{ fontSize: 12.5, color: C.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginTop: 2 }}>
                  Job #{j.jobNumber}
                  {j.title && ` · ${j.title}`}
                  {j.split > 1 && ` · split ${j.split} ways (${fmtMoney(j.amount)} total)`}
                </div>
              </div>
              <div style={{ fontSize: 13.5, color: C.inkSoft, whiteSpace: 'nowrap' }} title="Latest visit scheduled in this timeframe">
                {fmtShortDate(j.endAt)}
              </div>
              <div style={{ fontSize: 14.5, fontWeight: 700, color: '#15803D', textAlign: 'right', whiteSpace: 'nowrap' }}>
                {fmtMoney(j.share != null ? j.share : j.amount)}
              </div>
              <FaExternalLinkAlt style={{ fontSize: 11, color: C.muted }} />
            </a>
          ))}
        </div>
        )}
      </div>
    </div>
  );
}

// ── Manage Team modal ────────────────────────────────────────────
// Assignments live in Back4App (QuotaRosterEntry) and are edited here,
// not in Jobber. Each change closes the tech's current interval and
// opens a new one on the effective date, so history stays intact.

const SPEC_COLORS = {
  'Mechanical Service': TEAM_META.Ergi.avatarBg,
  'In-Suite Service': TEAM_META.Christina.avatarBg,
};

const ASSIGNMENT_OPTIONS = [
  { key: 'senior_mech', label: 'Senior Mechanical — $3,000/day' },
  { key: 'junior_mech', label: 'Junior Mechanical — $2,000/day' },
  { key: 'senior_insuite', label: 'Senior In-Suite — $1,750/day' },
  { key: 'junior_insuite', label: 'Junior In-Suite — $1,250/day' },
  { key: 'coordinator_mech', label: 'Mechanical Coordinator — leads a team' },
  { key: 'coordinator_insuite', label: 'In-Suite Coordinator — leads a team' },
  { key: 'projects', label: 'Projects — not tracked' },
  { key: 'vacation', label: 'Vacation — target paused' },
  { key: 'unassigned', label: 'Unassigned — not tracked' },
];

const MANAGE_GROUPS = [
  { title: 'Coordinators', match: (t) => t.assignment === 'coordinator_mech' || t.assignment === 'coordinator_insuite' },
  { title: 'Mechanical', match: (t) => t.assignment === 'senior_mech' || t.assignment === 'junior_mech' },
  { title: 'In-Suite', match: (t) => t.assignment === 'senior_insuite' || t.assignment === 'junior_insuite' },
  { title: 'On Vacation', match: (t) => t.assignment === 'vacation' },
  { title: 'Projects', match: (t) => t.assignment === 'projects' },
  { title: 'Unassigned', match: (t) => t.assignment === 'unassigned' },
];

// Which category an assignment belongs to, for filtering the
// coordinator dropdown next to each tech.
function categoryForAssignment(key) {
  if (key === 'senior_mech' || key === 'junior_mech' || key === 'coordinator_mech') return 'Mechanical Service';
  if (key === 'senior_insuite' || key === 'junior_insuite' || key === 'coordinator_insuite') return 'In-Suite Service';
  return null;
}

// Short label for an assignment key, e.g. "Senior Mechanical".
function assignmentLabel(key) {
  const o = ASSIGNMENT_OPTIONS.find((x) => x.key === key);
  return o ? o.label.split(' — ')[0] : key;
}

function ManageTeamModal({ onClose, onChanged }) {
  const [techs, setTechs] = useState(null);
  const [coordinators, setCoordinators] = useState([]);
  const [error, setError] = useState(null);
  const [savingId, setSavingId] = useState(null);
  // A requested change waiting for its effective date:
  // { tech, assignment, coordinatorId, leadName? }
  const [confirm, setConfirm] = useState(null);
  const [customDate, setCustomDate] = useState('');
  const dirtyRef = useRef(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const token = localStorage.getItem('managerToken') || '';
      const resp = await fetch(`${API_BASE_URL}/api/jobber/roster-admin`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok || !body.techs) throw new Error(body.error || `HTTP ${resp.status}`);
      setTechs(body.techs);
      setCoordinators(body.coordinators || []);
    } catch (e) {
      setError(String(e.message || e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const assign = async (tech, assignment, coordinatorId, effective) => {
    setSavingId(tech.jobberId);
    setError(null);
    try {
      const token = localStorage.getItem('managerToken') || '';
      const resp = await fetch(`${API_BASE_URL}/api/jobber/roster-admin/assign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          techJobberId: tech.jobberId,
          techName: tech.name,
          assignment,
          coordinatorId: coordinatorId || undefined,
          effectiveDate: effective || toISODate(new Date()),
        }),
      });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok || body.error) throw new Error(body.error || `HTTP ${resp.status}`);
      dirtyRef.current = true;
      await load();
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setSavingId(null);
    }
  };

  const applyConfirm = (effective) => {
    const c = confirm;
    setConfirm(null);
    setCustomDate('');
    if (c) assign(c.tech, c.assignment, c.coordinatorId, effective);
  };

  const cancelPending = async (tech) => {
    setSavingId(tech.jobberId);
    setError(null);
    try {
      const token = localStorage.getItem('managerToken') || '';
      const resp = await fetch(`${API_BASE_URL}/api/jobber/roster-admin/assign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ techJobberId: tech.jobberId, cancelPending: true }),
      });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok || body.error) throw new Error(body.error || `HTTP ${resp.status}`);
      dirtyRef.current = true;
      await load();
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setSavingId(null);
    }
  };

  const close = () => {
    if (dirtyRef.current && onChanged) onChanged();
    onClose();
  };

  return (
    <div
      className="secondary-mobile-overlay"
      onClick={close}
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        background: 'rgba(15,23,42,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: C.card, borderRadius: 14, boxShadow: '0 24px 64px rgba(15,23,42,.35)',
          width: 'min(840px, 100%)', maxHeight: '82vh', display: 'flex', flexDirection: 'column',
          overflow: 'hidden', fontFamily: sharedFont,
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
          padding: '16px 20px', borderBottom: `1px solid ${C.line}`, background: '#FAFBFD',
        }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: 16, color: C.ink }}>Manage Team</div>
            <div style={{ fontSize: 12.5, color: C.muted, marginTop: 2 }}>
              Changes apply from the effective date forward — earlier days keep the old team &amp; targets.
              Future dates schedule the change without touching today's quota.
            </div>
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              type="button"
              onClick={close}
              style={{
                width: 34, height: 34, borderRadius: 9,
                background: C.lineSoft, border: 'none', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.inkSoft,
              }}
            >
              <FaTimes />
            </button>
          </div>
        </div>

        {error && (
          <div style={{ padding: '10px 20px', fontSize: 12.5, color: C.red, background: C.redSoft }}>
            {error}
          </div>
        )}

        <div style={{ overflowY: 'auto' }}>
          {!techs && !error && (
            <div style={{ padding: '36px 24px', textAlign: 'center', fontSize: 13.5, color: C.muted }}>
              Loading team…
            </div>
          )}
          {techs && MANAGE_GROUPS.map((g) => {
            const members = techs.filter(g.match);
            if (!members.length) return null;
            return (
              <div key={g.title}>
                <div style={{
                  padding: '9px 20px', fontSize: 11.5, fontWeight: 800, color: C.muted,
                  textTransform: 'uppercase', letterSpacing: 0.6,
                  background: '#FAFBFD', borderBottom: `1px solid ${C.lineSoft}`,
                  borderTop: `1px solid ${C.lineSoft}`,
                }}>
                  {g.title} · {members.length}
                </div>
                {members.map((t) => {
                  const color = SPEC_COLORS[t.team] || '#9CA3AF';
                  const saving = savingId === t.jobberId;
                  const isCoordinator = t.assignment === 'coordinator_mech' || t.assignment === 'coordinator_insuite';
                  const category = categoryForAssignment(t.assignment) || t.team;
                  const categoryCoords = coordinators.filter((c) => c.category === category);
                  // The lead dropdown only matters once a category has
                  // more than one coordinator to choose between.
                  const showLead = !isCoordinator && category && categoryCoords.length > 1;
                  return (
                    <div
                      key={t.jobberId}
                      className="quota-team-mobile-row"
                      style={{
                        display: 'grid', gridTemplateColumns: showLead ? '28px 1fr 190px 150px' : '28px 1fr 230px',
                        alignItems: 'center', gap: 12, padding: '10px 20px',
                        borderBottom: `1px solid ${C.lineSoft}`,
                        opacity: saving ? 0.55 : 1,
                      }}
                    >
                      <div style={{
                        width: 26, height: 26, borderRadius: '50%', background: color, color: '#fff',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontWeight: 700, fontSize: 12,
                      }}>
                        {displayName(t.name)[0] ? displayName(t.name)[0].toUpperCase() : '?'}
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: 13.5, color: C.inkSoft, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {displayName(t.name)}
                        </div>
                        <div style={{ fontSize: 11, color: C.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {t.email || 'no email'}
                          {t.status === 'vacation' && t.role && ` · ${t.role}`}
                          {t.startDate && ` · since ${t.startDate}`}
                          {!showLead && t.coordinatorName && ` · lead: ${displayName(t.coordinatorName)}`}
                        </div>
                        {t.pending && (
                          <div style={{ fontSize: 11.5, color: C.amber, fontWeight: 700, marginTop: 2, whiteSpace: 'nowrap' }}>
                            ⏳ {assignmentLabel(t.pending.assignment)} from {t.pending.startDate}
                            <button
                              type="button"
                              title="Cancel this scheduled change"
                              disabled={saving}
                              onClick={() => cancelPending(t)}
                              style={{
                                marginLeft: 5, border: `1px solid ${C.amber}`, background: 'transparent',
                                color: C.amber, fontWeight: 800, cursor: 'pointer', borderRadius: 6,
                                fontSize: 10.5, padding: '1px 6px', lineHeight: 1.4, verticalAlign: 'middle',
                              }}
                            >
                              cancel
                            </button>
                          </div>
                        )}
                      </div>
                      <select
                        value={t.assignment}
                        disabled={saving}
                        onChange={(e) => setConfirm({ tech: t, assignment: e.target.value, coordinatorId: t.coordinatorId })}
                        style={{
                          fontFamily: sharedFont, fontSize: 12.5, fontWeight: 600, color: C.inkSoft,
                          border: `1px solid ${C.line}`, borderRadius: 9, padding: '8px 10px',
                          background: '#fff', cursor: saving ? 'wait' : 'pointer', outline: 'none',
                        }}
                      >
                        {ASSIGNMENT_OPTIONS.map((o) => (
                          <option key={o.key} value={o.key}>{o.label}</option>
                        ))}
                      </select>
                      {showLead && (
                        <select
                          value={t.coordinatorId || ''}
                          disabled={saving}
                          title="Which coordinator's team this tech counts toward"
                          onChange={(e) => setConfirm({
                            tech: t, assignment: t.assignment, coordinatorId: e.target.value,
                            leadName: displayName((coordinators.find((c) => c.jobberId === e.target.value) || {}).name || '').split(' ')[0],
                          })}
                          style={{
                            fontFamily: sharedFont, fontSize: 12.5, fontWeight: 600, color: C.inkSoft,
                            border: `1px solid ${C.line}`, borderRadius: 9, padding: '8px 10px',
                            background: '#fff', cursor: saving ? 'wait' : 'pointer', outline: 'none',
                          }}
                        >
                          {categoryCoords.map((c) => (
                            <option key={c.jobberId} value={c.jobberId}>
                              Lead: {displayName(c.name).split(' ')[0]}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div style={{
          padding: '11px 20px', borderTop: `1px solid ${C.line}`, background: '#FAFBFD',
          display: 'flex', alignItems: 'center', gap: 10, fontSize: 12, color: C.muted,
        }}>
          <span>Vacation keeps a tech visible with a paused target; Projects removes them from tracking.</span>
          <a
            href="https://secure.getjobber.com/manage_team"
            target="_blank"
            rel="noreferrer"
            style={{ marginLeft: 'auto', color: C.blue, fontWeight: 600, textDecoration: 'none', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 6 }}
          >
            Open in Jobber <FaExternalLinkAlt style={{ fontSize: 10 }} />
          </a>
        </div>
      </div>

      {/* Effective-date confirmation for a requested change */}
      {confirm && (() => {
        const today = toISODate(new Date());
        const tmrw = new Date();
        tmrw.setDate(tmrw.getDate() + 1);
        const tomorrow = toISODate(tmrw);
        // Late in the day the quota is mostly earned — recommend
        // starting the change tomorrow so today's targets stay intact.
        const recommend = new Date().getHours() >= 10 ? tomorrow : today;
        const choice = (label, sub, dateVal) => {
          const rec = dateVal === recommend;
          return (
            <button
              type="button"
              onClick={() => applyConfirm(dateVal)}
              style={{
                display: 'block', width: '100%', textAlign: 'left',
                fontFamily: sharedFont, border: `1.5px solid ${rec ? C.navy : C.line}`,
                background: rec ? C.blueSoft : '#fff', borderRadius: 10,
                padding: '11px 14px', cursor: 'pointer', marginBottom: 8,
              }}
            >
              <span style={{ fontWeight: 800, fontSize: 14, color: rec ? C.navy : C.ink }}>
                {label}{rec ? ' · recommended' : ''}
              </span>
              <span style={{ display: 'block', fontSize: 12, color: C.muted, marginTop: 2 }}>{sub}</span>
            </button>
          );
        };
        return (
          <div
            onClick={() => { setConfirm(null); setCustomDate(''); }}
            style={{
              position: 'fixed', inset: 0, zIndex: 300,
              background: 'rgba(15,23,42,0.4)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: '#fff', borderRadius: 14, boxShadow: '0 24px 64px rgba(15,23,42,.4)',
                width: 'min(400px, 100%)', padding: '20px 22px', fontFamily: sharedFont,
              }}
            >
              <div style={{ fontWeight: 800, fontSize: 15.5, color: C.ink }}>
                {confirm.leadName
                  ? `Move ${displayName(confirm.tech.name)} to Lead: ${confirm.leadName}`
                  : `${displayName(confirm.tech.name)} → ${assignmentLabel(confirm.assignment)}`}
              </div>
              <div style={{ fontSize: 12.5, color: C.muted, margin: '4px 0 16px' }}>
                When should this take effect?
              </div>

              {choice('Today', "Applies to today — reduces today's quota immediately", today)}
              {choice('Tomorrow', "Today's quota stays as-is; switches at midnight", tomorrow)}

              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                <input
                  type="date"
                  value={customDate}
                  max={toISODate(new Date(Date.now() + 90 * 86400000))}
                  onChange={(e) => setCustomDate(e.target.value)}
                  style={{
                    flex: 1, fontFamily: sharedFont, fontSize: 12.5, border: `1px solid ${C.line}`,
                    borderRadius: 9, padding: '9px 10px', color: C.inkSoft, outline: 'none',
                  }}
                />
                <button
                  type="button"
                  disabled={!customDate}
                  onClick={() => applyConfirm(customDate)}
                  style={{
                    fontFamily: sharedFont, fontSize: 12.5, fontWeight: 700,
                    color: customDate ? '#fff' : C.muted,
                    background: customDate ? C.navy : C.lineSoft,
                    border: 'none', borderRadius: 9, padding: '9px 14px',
                    cursor: customDate ? 'pointer' : 'default',
                  }}
                >
                  Use date
                </button>
              </div>

              <button
                type="button"
                onClick={() => { setConfirm(null); setCustomDate(''); }}
                style={{
                  display: 'block', margin: '14px auto 0', border: 'none', background: 'transparent',
                  fontFamily: sharedFont, fontSize: 12.5, fontWeight: 600, color: C.muted, cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// ── Manage Skills modal ──────────────────────────────────────────
// Who can do what — fan coils vs heat pumps, boilers vs chillers, etc.
// So a new coordinator can see capabilities at a glance without asking
// around. Click a cell to toggle; saves immediately.

const SKILL_FILTERS = [
  { key: 'all', label: 'Everyone' },
  { key: 'service', label: 'Service teams' },
  { key: 'mechanical', label: 'Mechanical' },
  { key: 'insuite', label: 'In-Suite' },
];

const SKILL_GROUP_HEADER_H = 28;
const SKILL_LABEL_HEADER_H = 64;
const SKILL_COL_W = 78;

function skillFilterMatch(filter, tech) {
  const a = tech.assignment || '';
  const team = tech.team || '';
  if (filter === 'all') return true;
  if (filter === 'mechanical') {
    return team === 'Mechanical Service'
      || a === 'senior_mech' || a === 'junior_mech' || a === 'coordinator_mech';
  }
  if (filter === 'insuite') {
    return team === 'In-Suite Service'
      || a === 'senior_insuite' || a === 'junior_insuite' || a === 'coordinator_insuite';
  }
  // service teams: active mech/insuite + coordinators + vacation
  return [
    'senior_mech', 'junior_mech', 'senior_insuite', 'junior_insuite',
    'coordinator_mech', 'coordinator_insuite', 'vacation',
  ].includes(a);
}

function ManageSkillsModal({ onClose }) {
  const [skills, setSkills] = useState(null);
  const [techs, setTechs] = useState(null);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  // Click a skill column: sort (capable first) → filter (only capable) → clear.
  const [skillCol, setSkillCol] = useState(null); // catalog key
  const [skillColMode, setSkillColMode] = useState('sort'); // 'sort' | 'filter'
  const [savingKey, setSavingKey] = useState(null); // `${jobberId}:${skillKey}`

  const load = useCallback(async () => {
    setError(null);
    try {
      const token = localStorage.getItem('managerToken') || '';
      const resp = await fetch(`${API_BASE_URL}/api/jobber/tech-skills`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok || !body.techs) throw new Error(body.error || `HTTP ${resp.status}`);
      setSkills(body.skills || []);
      setTechs(body.techs);
    } catch (e) {
      setError(String(e.message || e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const cycleSkillCol = (key) => {
    if (skillCol !== key) {
      setSkillCol(key);
      setSkillColMode('sort');
      return;
    }
    if (skillColMode === 'sort') {
      setSkillColMode('filter');
      return;
    }
    setSkillCol(null);
    setSkillColMode('sort');
  };

  const clearSkillCol = () => {
    setSkillCol(null);
    setSkillColMode('sort');
  };

  const toggle = async (tech, skillKey) => {
    const has = (tech.skills || []).includes(skillKey);
    const next = has
      ? (tech.skills || []).filter((k) => k !== skillKey)
      : [...(tech.skills || []), skillKey];

    // Optimistic update so the matrix feels snappy.
    setTechs((prev) => (prev || []).map((t) => (
      t.jobberId === tech.jobberId ? { ...t, skills: next } : t
    )));
    setSavingKey(`${tech.jobberId}:${skillKey}`);
    setError(null);
    try {
      const token = localStorage.getItem('managerToken') || '';
      const resp = await fetch(`${API_BASE_URL}/api/jobber/tech-skills`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          techJobberId: tech.jobberId,
          techName: tech.name,
          skills: next,
        }),
      });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok || body.error) throw new Error(body.error || `HTTP ${resp.status}`);
    } catch (e) {
      // Roll back this tech's skills by reloading.
      setError(String(e.message || e));
      await load();
    } finally {
      setSavingKey(null);
    }
  };

  const groups = useMemo(() => {
    const list = skills || [];
    const order = ['In-Suite', 'Mechanical'];
    return order
      .map((name) => ({ name, items: list.filter((s) => s.group === name) }))
      .filter((g) => g.items.length);
  }, [skills]);

  const skillColLabel = useMemo(() => {
    if (!skillCol || !skills) return '';
    const hit = skills.find((s) => s.key === skillCol);
    return hit ? hit.label : skillCol;
  }, [skillCol, skills]);

  const shown = useMemo(() => {
    let list = (techs || []).filter((t) => skillFilterMatch(filter, t));
    if (skillCol && skillColMode === 'filter') {
      list = list.filter((t) => (t.skills || []).includes(skillCol));
    }
    if (skillCol) {
      list = [...list].sort((a, b) => {
        const aOn = (a.skills || []).includes(skillCol) ? 0 : 1;
        const bOn = (b.skills || []).includes(skillCol) ? 0 : 1;
        if (aOn !== bOn) return aOn - bOn;
        return (a.name || '').localeCompare(b.name || '');
      });
    }
    return list;
  }, [techs, filter, skillCol, skillColMode]);

  const capableCount = useMemo(() => {
    if (!skillCol) return 0;
    return (techs || [])
      .filter((t) => skillFilterMatch(filter, t))
      .filter((t) => (t.skills || []).includes(skillCol)).length;
  }, [techs, filter, skillCol]);

  return (
    <div
      className="secondary-mobile-overlay"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        background: 'rgba(15,23,42,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        // Clear the hub sidebar (240) so the sheet isn't glued to it.
        padding: '24px 24px 24px 264px',
        boxSizing: 'border-box',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: C.card, borderRadius: 14, boxShadow: '0 24px 64px rgba(15,23,42,.35)',
          width: 'min(1100px, 100%)', maxHeight: '88vh', display: 'flex', flexDirection: 'column',
          overflow: 'hidden', fontFamily: sharedFont,
        }}
      >
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap',
          padding: '16px 20px', borderBottom: `1px solid ${C.line}`, background: '#FAFBFD',
        }}>
          <div style={{ minWidth: 0, flex: '1 1 240px' }}>
            <div style={{ fontWeight: 800, fontSize: 16, color: C.ink }}>Technician skills</div>
            <div style={{ fontSize: 12.5, color: C.muted, marginTop: 2 }}>
              Click a cell to mark skills. Click a column header to sort / filter who can do it.
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            {SKILL_FILTERS.map((f) => {
              const on = filter === f.key;
              return (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => setFilter(f.key)}
                  style={{
                    fontFamily: sharedFont, fontSize: 12.5, fontWeight: 700,
                    padding: '7px 12px', borderRadius: 999, cursor: 'pointer',
                    border: `1px solid ${on ? C.navy : C.line}`,
                    background: on ? C.navy : C.card,
                    color: on ? '#fff' : C.inkSoft,
                  }}
                >
                  {f.label}
                </button>
              );
            })}
            <button
              type="button"
              onClick={onClose}
              style={{
                width: 34, height: 34, borderRadius: 9, marginLeft: 4,
                background: C.lineSoft, border: 'none', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.inkSoft,
              }}
            >
              <FaTimes />
            </button>
          </div>
        </div>

        {error && (
          <div style={{ padding: '10px 20px', fontSize: 12.5, color: C.red, background: C.redSoft }}>
            {error}
          </div>
        )}

        {skillCol && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
            padding: '8px 20px', fontSize: 12.5, color: C.inkSoft,
            background: '#ECFDF5', borderBottom: `1px solid ${C.line}`,
          }}>
            <span style={{ fontWeight: 700, color: C.green }}>{skillColLabel}</span>
            <span>
              {skillColMode === 'filter'
                ? `Showing ${shown.length} capable`
                : `Capable first · ${capableCount} marked`}
            </span>
            <span style={{ color: C.muted }}>
              {skillColMode === 'sort' ? 'Click header again to show only capable' : 'Click header again to clear'}
            </span>
            <button
              type="button"
              onClick={clearSkillCol}
              style={{
                marginLeft: 'auto', fontFamily: sharedFont, fontSize: 12, fontWeight: 700,
                padding: '4px 10px', borderRadius: 8, cursor: 'pointer',
                border: `1px solid ${C.line}`, background: C.card, color: C.inkSoft,
              }}
            >
              Clear
            </button>
          </div>
        )}

        <div style={{ overflow: 'auto', flex: 1, WebkitOverflowScrolling: 'touch' }}>
          {!techs && !error && (
            <div style={{ padding: '36px 24px', textAlign: 'center', fontSize: 13.5, color: C.muted }}>
              Loading skills…
            </div>
          )}
          {techs && !shown.length && (
            <div style={{ padding: '36px 24px', textAlign: 'center', fontSize: 13.5, color: C.muted }}>
              {skillCol && skillColMode === 'filter'
                ? `No one marked for ${skillColLabel} yet.`
                : 'No technicians in this filter.'}
            </div>
          )}
          {techs && shown.length > 0 && skills && (
            <table style={{ borderCollapse: 'separate', borderSpacing: 0, width: 'max-content' }}>
              <thead>
                <tr>
                  <th
                    rowSpan={2}
                    style={{
                      position: 'sticky', left: 0, top: 0, zIndex: 4,
                      background: '#FAFBFD', borderBottom: `1px solid ${C.line}`,
                      borderRight: `1px solid ${C.line}`,
                      padding: '10px 14px', textAlign: 'left',
                      fontSize: 11, fontWeight: 700, color: C.muted,
                      textTransform: 'uppercase', letterSpacing: 0.5,
                      width: 190, minWidth: 190, verticalAlign: 'bottom',
                    }}
                  >
                    Technician
                  </th>
                  {groups.map((g) => (
                    <th
                      key={g.name}
                      colSpan={g.items.length}
                      style={{
                        position: 'sticky', top: 0, zIndex: 3,
                        height: SKILL_GROUP_HEADER_H,
                        background: g.name === 'Mechanical' ? '#EEF2FF'
                          : g.name === 'In-Suite' ? '#ECFDF5' : '#FFF7ED',
                        borderBottom: `1px solid ${C.line}`,
                        borderLeft: `1px solid ${C.lineSoft}`,
                        padding: '0 8px',
                        fontSize: 11, fontWeight: 800, color: C.inkSoft,
                        textTransform: 'uppercase', letterSpacing: 0.4,
                        textAlign: 'center',
                        lineHeight: `${SKILL_GROUP_HEADER_H}px`,
                        boxSizing: 'border-box',
                      }}
                    >
                      {g.name}
                    </th>
                  ))}
                </tr>
                <tr>
                  {groups.flatMap((g) => g.items.map((s) => {
                    const active = skillCol === s.key;
                    return (
                      <th
                        key={s.key}
                        title={
                          active
                            ? (skillColMode === 'sort'
                              ? `${s.label}: click again to show only capable`
                              : `${s.label}: click again to clear`)
                            : `${s.label}: click to sort capable first`
                        }
                        onClick={() => cycleSkillCol(s.key)}
                        style={{
                          position: 'sticky', top: SKILL_GROUP_HEADER_H, zIndex: 3,
                          height: SKILL_LABEL_HEADER_H,
                          width: SKILL_COL_W, minWidth: SKILL_COL_W, maxWidth: SKILL_COL_W,
                          background: active ? '#DCFCE7' : '#FAFBFD',
                          borderBottom: `2px solid ${active ? C.green : C.line}`,
                          borderLeft: `1px solid ${C.lineSoft}`,
                          padding: '6px 4px',
                          fontSize: 11, fontWeight: 700,
                          color: active ? C.green : C.inkSoft,
                          textAlign: 'center', verticalAlign: 'middle',
                          lineHeight: 1.25, whiteSpace: 'normal',
                          boxSizing: 'border-box',
                          overflow: 'hidden',
                          cursor: 'pointer',
                          userSelect: 'none',
                        }}
                      >
                        {s.label}
                        {active && (
                          <div style={{
                            fontSize: 9, fontWeight: 800, marginTop: 2,
                            textTransform: 'uppercase', letterSpacing: 0.3,
                            color: C.green,
                          }}>
                            {skillColMode === 'filter' ? 'only' : 'sort'}
                          </div>
                        )}
                      </th>
                    );
                  }))}
                </tr>
              </thead>
              <tbody>
                {shown.map((tech) => {
                  const can = new Set(tech.skills || []);
                  return (
                    <tr key={tech.jobberId}>
                      <td
                        style={{
                          position: 'sticky', left: 0, zIndex: 1,
                          background: C.card, borderBottom: `1px solid ${C.lineSoft}`,
                          borderRight: `1px solid ${C.line}`,
                          padding: '8px 14px', whiteSpace: 'nowrap',
                          width: 190, minWidth: 190,
                        }}
                      >
                        <div style={{ fontWeight: 700, fontSize: 13.5, color: C.ink }}>
                          {displayName(tech.name)}
                        </div>
                        <div style={{ fontSize: 11.5, color: C.muted, marginTop: 1 }}>
                          {tech.role || assignmentLabel(tech.assignment)}
                          {tech.team ? ` · ${tech.team === 'Mechanical Service' ? 'Mech' : 'In-Suite'}` : ''}
                        </div>
                      </td>
                      {groups.flatMap((g) => g.items.map((s) => {
                        const on = can.has(s.key);
                        const busy = savingKey === `${tech.jobberId}:${s.key}`;
                        const colActive = skillCol === s.key;
                        return (
                          <td
                            key={s.key}
                            style={{
                              borderBottom: `1px solid ${C.lineSoft}`,
                              borderLeft: `1px solid ${C.lineSoft}`,
                              textAlign: 'center', padding: 4,
                              width: SKILL_COL_W, minWidth: SKILL_COL_W, maxWidth: SKILL_COL_W,
                              background: colActive ? '#F0FDF4' : 'transparent',
                              boxSizing: 'border-box',
                            }}
                          >
                            <button
                              type="button"
                              title={on ? `${s.label}: capable — click to clear` : `${s.label}: not marked — click to add`}
                              disabled={busy}
                              onClick={() => toggle(tech, s.key)}
                              style={{
                                width: 30, height: 30, borderRadius: 8,
                                border: `1px solid ${on ? '#86efac' : C.line}`,
                                background: on ? '#DCFCE7' : C.card,
                                color: on ? C.green : C.muted,
                                cursor: busy ? 'wait' : 'pointer',
                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                opacity: busy ? 0.55 : 1,
                              }}
                            >
                              {on ? <FaCheck size={12} /> : <span style={{ fontSize: 14, lineHeight: 1 }}>–</span>}
                            </button>
                          </td>
                        );
                      }))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function UnsentTeamRow({ name, teamName, specialty, techCount, avatarBg, agg, expanded, onToggle }) {
  return (
    <div style={{ borderBottom: `1px solid ${C.lineSoft}` }}>
      <div className="quota-mobile-row"
        onClick={onToggle}
        style={{
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '15px 22px', cursor: 'pointer',
          background: expanded ? '#FAFBFD' : 'transparent',
        }}
      >
        <FaChevronDown style={{
          fontSize: 12, color: C.muted, flexShrink: 0,
          transition: 'transform .2s ease',
          transform: expanded ? 'rotate(180deg)' : 'none',
        }} />
        <TeamAvatar specialty={specialty} bg={avatarBg} />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 800, fontSize: 15.5, color: C.ink }}>{teamName || name}</div>
          <div style={{ fontSize: 14, fontWeight: 600, color: C.inkSoft, marginTop: 3 }}>Lead: {name}</div>
          {techCount != null && (
            <div style={{ marginTop: 3 }}>
              <span style={{
                display: 'inline-block', fontSize: 12.5, fontWeight: 700, color: C.navy,
                background: '#DBEAFE', borderRadius: 999, padding: '2px 10px',
              }}>
                {techCount} techs
              </span>
            </div>
          )}
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 14 }}>
          <span style={{
            fontSize: 15, fontWeight: 800,
            color: agg.count > 0 ? C.red : C.muted,
            background: agg.count > 0 ? C.redSoft : 'transparent',
            padding: '4px 12px', borderRadius: 999, whiteSpace: 'nowrap',
          }}>
            {agg.count} job{agg.count === 1 ? '' : 's'}
          </span>
          <span style={{ fontWeight: 800, fontSize: 15, color: agg.total > 0 ? C.red : C.green }}>
            {fmtMoney(agg.total)}
          </span>
        </div>
      </div>
      {expanded && (
        <div style={{ background: '#F1F4F9', borderTop: `1px solid ${C.line}` }}>
          <UnsentJobsList jobs={agg.jobs || []} />
        </div>
      )}
    </div>
  );
}

// ── Sortable list headers (date / amount) ────────────────────────
const colHeadStyle = {
  fontSize: 11, fontWeight: 700, color: C.muted,
  textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap',
};

function SortableCol({ label, colKey, sort, onSort, align = 'left' }) {
  const active = sort.key === colKey;
  const Icon = !active ? FaSort : sort.dir === 'asc' ? FaSortUp : FaSortDown;
  return (
    <button
      type="button"
      onClick={() => onSort(colKey)}
      title={`Sort by ${label.toLowerCase()}`}
      style={{
        ...colHeadStyle,
        display: 'inline-flex', alignItems: 'center', gap: 4, width: '100%',
        justifyContent: align === 'right' ? 'flex-end' : 'flex-start',
        background: 'none', border: 'none', padding: 0, cursor: 'pointer',
        fontFamily: sharedFont, color: active ? C.navy : C.muted,
      }}
    >
      {label}
      <Icon style={{ fontSize: 11, opacity: active ? 1 : 0.45 }} />
    </button>
  );
}

function toggleSort(sort, key) {
  if (sort.key === key) return { key, dir: sort.dir === 'desc' ? 'asc' : 'desc' };
  return { key, dir: 'desc' };
}

// `textOf` maps extra sortable column keys (e.g. sales, tech) to a
// string getter for that column.
function sortedBy(rows, sort, dateOf, amountOf, textOf) {
  const mul = sort.dir === 'asc' ? 1 : -1;
  const textGetter = textOf && textOf[sort.key];
  return [...rows].sort((a, b) => {
    if (sort.key === 'amount') return mul * ((amountOf(a) || 0) - (amountOf(b) || 0));
    if (textGetter) {
      return mul * String(textGetter(a) || '').localeCompare(String(textGetter(b) || ''));
    }
    return mul * String(dateOf(a) || '').localeCompare(String(dateOf(b) || ''));
  });
}

// Expandable "view every invoice counted for this team" list, shown
// inside each team's accordion. Makes team totals auditable —
// including invoices credited via salesperson that don't appear
// under any tech.
function TeamInvoicesSection({ invoices, context = 'for this team' }) {
  const [open, setOpen] = useState(false);
  const [sort, setSort] = useState({ key: 'date', dir: 'desc' });
  if (!invoices || invoices.length === 0) return null;

  const total = invoices.reduce((s, i) => s + (i.total || 0), 0);
  const onSort = (k) => setSort((s) => toggleSort(s, k));
  const rows = sortedBy(invoices, sort, (i) => i.workDate || i.issuedDate, (i) => i.total);

  return (
    <div style={{ borderTop: `1px solid ${C.lineSoft}` }}>
      <div
        onClick={() => setOpen((o) => !o)}
        style={{
          padding: '10px 22px 10px 44px', fontSize: 12.5, color: C.muted, cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 8,
        }}
      >
        <FaChevronDown style={{
          fontSize: 10, transition: 'transform .2s ease',
          transform: open ? 'rotate(180deg)' : 'none',
        }} />
        <span>
          {invoices.length} invoice{invoices.length === 1 ? '' : 's'} totalling{' '}
          <span style={{ fontWeight: 700, color: C.inkSoft }}>{fmtMoney(total)}</span>
          {' '}counted {context}. Click to see them all.
        </span>
      </div>
      {open && (
        <div className="internal-data-scroll" role="region" aria-label="Invoice details, scroll for all columns" tabIndex={0} style={{ borderTop: `1px solid ${C.lineSoft}`, background: '#FAFBFD', overflowX: 'auto' }}>
        <div style={{ minWidth: 640 }}>
          <div style={{
            display: 'grid', gridTemplateColumns: '16px 1fr 170px 120px 110px 14px',
            alignItems: 'center', gap: 14, padding: '8px 22px 7px 44px',
            borderBottom: '1px solid #D9E0EA',
          }}>
            <span />
            <span style={colHeadStyle}>Client / Invoice</span>
            <span style={colHeadStyle}>Technician</span>
            <SortableCol label="Date" colKey="date" sort={sort} onSort={onSort} />
            <SortableCol label="Amount" colKey="amount" sort={sort} onSort={onSort} align="right" />
            <span />
          </div>
          {rows.map((inv) => (
            <a
              key={`${inv.invoiceNumber}-${inv.url}`}
              href={inv.url || '#'}
              target="_blank"
              rel="noreferrer"
              title="Open invoice in Jobber (new tab)"
              style={{
                display: 'grid',
                gridTemplateColumns: '16px 1fr 170px 120px 110px 14px',
                alignItems: 'center',
                gap: 14,
                padding: '13px 22px 13px 44px',
                borderBottom: '1px solid #D9E0EA',
                textDecoration: 'none',
                color: 'inherit',
                cursor: 'pointer',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#EDF1F7'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              <FaFileInvoiceDollar style={{ fontSize: 15, color: C.muted }} />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: C.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {inv.client || '—'}
                </div>
                <div style={{ fontSize: 12.5, color: C.muted, whiteSpace: 'nowrap', marginTop: 2 }}>
                  Invoice #{inv.invoiceNumber}
                </div>
              </div>
              <div
                title={inv.techs && inv.techs.length ? inv.techs.map(displayName).join(', ') : 'No tech on invoice'}
                style={{
                  fontSize: 13.5, color: inv.techs && inv.techs.length ? C.inkSoft : C.muted,
                  fontWeight: inv.techs && inv.techs.length ? 600 : 400,
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}
              >
                {inv.techs && inv.techs.length ? inv.techs.map(displayName).join(', ') : 'None'}
              </div>
              <div style={{ fontSize: 13.5, color: C.inkSoft, whiteSpace: 'nowrap' }} title="Date the work was completed">
                {fmtShortDate(inv.workDate || inv.issuedDate)}
              </div>
              <div style={{ fontSize: 14.5, fontWeight: 700, color: C.ink, textAlign: 'right' }}>
                {fmtMoney(inv.total)}
              </div>
              <FaExternalLinkAlt style={{ fontSize: 11, color: C.muted }} />
            </a>
          ))}
        </div>
        </div>
      )}
    </div>
  );
}

// Expandable "couldn't attribute these invoices" footer.
function UnattributedSection({ unassigned }) {
  const [open, setOpen] = useState(false);
  const [sort, setSort] = useState({ key: 'date', dir: 'desc' });
  if (!unassigned || unassigned.count === 0) return null;
  const onSort = (k) => setSort((s) => toggleSort(s, k));
  const invoices = sortedBy(
    unassigned.invoices || [], sort,
    (inv) => inv.workDate || inv.issuedDate,
    (inv) => inv.total
  );

  return (
    <div style={{ background: '#FAFBFD', borderTop: `1px solid ${C.lineSoft}` }}>
      <div
        onClick={() => setOpen((o) => !o)}
        style={{
          padding: '11px 22px', fontSize: 12.5, color: C.muted, cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 8,
        }}
      >
        <FaChevronDown style={{
          fontSize: 10, transition: 'transform .2s ease',
          transform: open ? 'rotate(180deg)' : 'none',
        }} />
        <span>
          {unassigned.count} invoice{unassigned.count === 1 ? '' : 's'} couldn't be attributed to a team (no salesperson and no assigned tech).
        </span>
      </div>
      {open && (
        <div style={{ borderTop: `1px solid ${C.lineSoft}` }}>
          <div className="quota-invoice-mobile-header" style={{
            display: 'grid', gridTemplateColumns: '16px 1fr 120px 110px 14px',
            alignItems: 'center', gap: 14, padding: '8px 22px 7px 40px',
            borderBottom: '1px solid #D9E0EA',
          }}>
            <span />
            <span style={colHeadStyle}>Client / Invoice</span>
            <SortableCol label="Date" colKey="date" sort={sort} onSort={onSort} />
            <SortableCol label="Amount" colKey="amount" sort={sort} onSort={onSort} align="right" />
            <span />
          </div>
          {invoices.map((inv) => (
            <a
              key={`${inv.invoiceNumber}-${inv.url}`}
              href={inv.url || '#'}
              target="_blank"
              rel="noreferrer"
              title="Open invoice in Jobber (new tab)"
              className="quota-invoice-mobile-row"
              style={{
                display: 'grid',
                gridTemplateColumns: '16px 1fr 120px 110px 14px',
                alignItems: 'center',
                gap: 14,
                padding: '9px 22px 9px 40px',
                borderBottom: `1px solid ${C.lineSoft}`,
                textDecoration: 'none',
                color: 'inherit',
                cursor: 'pointer',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#EDF1F7'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              <FaFileInvoiceDollar style={{ fontSize: 13, color: C.muted }} />
              <div style={{ minWidth: 0 }}>
                <span style={{ fontSize: 12.5, fontWeight: 600, color: C.ink }}>
                  {inv.client || '—'}
                </span>
                <span style={{ fontSize: 11.5, color: C.muted }}> · Invoice #{inv.invoiceNumber}</span>
              </div>
              <div style={{ fontSize: 12, color: C.inkSoft }} title="Date the work was completed">
                {fmtShortDate(inv.workDate || inv.issuedDate)}
              </div>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: C.ink, textAlign: 'right' }}>
                {fmtMoney(inv.total)}
              </div>
              <FaExternalLinkAlt style={{ fontSize: 11, color: C.muted }} />
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Weekly performance scoreboard ────────────────────────────────
// One row per calendar week since tracking began; each week scored on
// its own (total invoiced vs. target, and the % above/below target).

function weekLabel(startIso, endIso) {
  const s = new Date(startIso + 'T00:00:00');
  const e = new Date(endIso + 'T00:00:00');
  return `${fmtDate(s, false)} – ${fmtDate(e, false)}`;
}

function DiffBadge({ invoiced, target }) {
  if (!target) return <span style={{ fontSize: 12, color: C.muted }}>—</span>;
  const diff = (invoiced / target) * 100 - 100;
  const hit = diff >= 0;
  return (
    <span style={{
      display: 'inline-block', fontSize: 12, fontWeight: 800,
      padding: '2px 8px', borderRadius: 999,
      background: hit ? C.greenSoft : C.redSoft,
      color: hit ? C.green : C.red,
    }}>
      {hit ? '+' : '−'}{Math.abs(diff).toFixed(0)}%
    </span>
  );
}

function InProgressPill() {
  return (
    <span style={{
      display: 'inline-block', fontSize: 10.5, fontWeight: 800,
      padding: '1px 8px', borderRadius: 999, background: '#DBEAFE', color: C.navy,
      textTransform: 'uppercase', letterSpacing: 0.5,
    }}>
      In progress
    </span>
  );
}

// One week inside a team's performance history. Expands to the
// per-tech breakdown plus every invoice counted that week.
function PerfWeekRow({ week, agg, color }) {
  const [open, setOpen] = useState(false);
  if (!agg) return null;

  const techs = Object.entries(agg.techs || {})
    .map(([id, t]) => ({ id, ...t }))
    .sort((a, b) => b.invoiced - a.invoiced || b.target - a.target);
  const techSum = techs.reduce((s, t) => s + t.invoiced, 0);
  const teamOnly = agg.invoiced - techSum;

  return (
    <div style={{ borderBottom: '1px solid #D9E0EA' }}>
      <div
        onClick={() => setOpen((o) => !o)}
        title="See each tech's numbers and the invoices for this week"
        onMouseEnter={(e) => { e.currentTarget.style.background = '#E9EEF5'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
        style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '16px 22px 16px 44px', cursor: 'pointer',
        }}
      >
        <FaChevronDown style={{
          fontSize: 10, color: C.muted, flexShrink: 0,
          transition: 'transform .2s ease',
          transform: open ? 'rotate(180deg)' : 'none',
        }} />
        <span style={{ fontWeight: 700, fontSize: 14.5, color: C.ink, whiteSpace: 'nowrap' }}>
          {weekLabel(week.start, week.end)}
        </span>
        {week.current && <InProgressPill />}
        <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 14 }}>
          {agg.target > 0 && (() => {
            // Where "now" sits in the week: workdays elapsed / workdays
            // total, marked with a caret so you can eyeball fill vs pace.
            const wkStart = new Date(week.start + 'T00:00:00');
            const wkEnd = new Date(week.end + 'T23:59:59');
            const now = new Date();
            const totalDays = Math.max(1, weekdaysBetween(wkStart, wkEnd));
            const elapsedDays = now >= wkEnd
              ? totalDays
              : weekdaysBetween(wkStart, now < wkStart ? wkStart : now);
            const pacePct = Math.min(100, (elapsedDays / totalDays) * 100);
            return (
              <span
                title={`${Math.min(100, Math.round((agg.invoiced / agg.target) * 100))}% of this week's target — caret marks day ${elapsedDays} of ${totalDays}`}
                style={{ position: 'relative', width: 220, height: 5, flexShrink: 0 }}
              >
                <span style={{
                  position: 'absolute', inset: 0, borderRadius: 999,
                  background: '#E4E9F0', overflow: 'hidden',
                }}>
                  <span style={{
                    display: 'block', height: '100%', borderRadius: 999, background: color,
                    width: `${Math.min(100, (agg.invoiced / agg.target) * 100)}%`,
                    transition: 'width .4s ease',
                  }} />
                </span>
                {week.current && (
                  <span style={{
                    position: 'absolute', top: -7, left: `calc(${pacePct}% - 4px)`,
                    width: 0, height: 0,
                    borderLeft: '4px solid transparent',
                    borderRight: '4px solid transparent',
                    borderTop: '5px solid #475569',
                  }} />
                )}
              </span>
            );
          })()}
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontWeight: 800, fontSize: 15, color: C.ink }}>{fmtMoney(agg.invoiced)}</span>
            <span style={{ fontSize: 13, color: C.muted }}>of {fmtMoney(agg.target)}</span>
            <DiffBadge invoiced={agg.invoiced} target={agg.target} />
          </span>
        </span>
      </div>

      {open && (
        <div style={{ background: '#FAFBFD', borderTop: `1px solid ${C.lineSoft}` }}>
          {techs.map((t) => (
            <div key={t.id} style={{
              display: 'grid', gridTemplateColumns: '26px 1fr auto',
              alignItems: 'center', gap: 12,
              padding: '11px 22px 11px 62px', borderBottom: '1px solid #D9E0EA',
            }}>
              <div style={{
                width: 26, height: 26, borderRadius: '50%', background: color, color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 700, fontSize: 12, flexShrink: 0,
              }}>
                {displayName(t.name)[0] ? displayName(t.name)[0].toUpperCase() : '—'}
              </div>
              <div style={{ fontSize: 14, fontWeight: 600, color: C.inkSoft, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {displayName(t.name)}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontWeight: 700, fontSize: 14, color: C.ink }}>{fmtMoney(t.invoiced)}</span>
                <span style={{ fontSize: 12.5, color: C.muted }}>of {fmtMoney(t.target)}</span>
                <DiffBadge invoiced={t.invoiced} target={t.target} />
              </div>
            </div>
          ))}
          {techs.length === 0 && (
            <div style={{ padding: '12px 22px 12px 62px', fontSize: 12.5, color: C.muted, borderBottom: `1px solid ${C.lineSoft}` }}>
              No tech had credited work this week.
            </div>
          )}
          {teamOnly > 0.5 && (
            <div style={{
              padding: '8px 22px 8px 62px', fontSize: 11.5, color: C.muted,
              borderBottom: `1px solid ${C.lineSoft}`,
            }}>
              {fmtMoney(teamOnly)} more was credited to the team via salesperson without a matching tech on the invoice.
            </div>
          )}
          <TeamInvoicesSection invoices={agg.invoices || []} context="this week" />
        </div>
      )}
    </div>
  );
}

// One team's performance history: a row styled like the quota /
// unsent sections, starting closed. The header shows the current
// week; expanding lists every tracked week.
function PerfTeamRow({ coord, weeks }) {
  const [expanded, setExpanded] = useState(false);
  const current = weeks.find((w) => w.current);
  const currentAgg = current ? current.teams[coord.key] : null;

  return (
    <div style={{ borderBottom: `1px solid ${C.lineSoft}` }}>
      <div
        onClick={() => setExpanded((o) => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '15px 22px', cursor: 'pointer',
          background: expanded ? '#FAFBFD' : 'transparent',
        }}
      >
        <FaChevronDown style={{
          fontSize: 12, color: C.muted, flexShrink: 0,
          transition: 'transform .2s ease',
          transform: expanded ? 'rotate(180deg)' : 'none',
        }} />
        <TeamAvatar specialty={coord.specialty} bg={coord.avatarBg} />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 800, fontSize: 15.5, color: C.ink }}>{coord.teamLabel || coord.name}</div>
          <div style={{ fontSize: 14, fontWeight: 600, color: C.inkSoft, marginTop: 3 }}>Lead: {coord.name}</div>
          <div style={{ marginTop: 3 }}>
            <span style={{
              display: 'inline-block', fontSize: 12.5, fontWeight: 700, color: C.navy,
              background: '#DBEAFE', borderRadius: 999, padding: '2px 10px',
            }}>
              {weeks.length} week{weeks.length === 1 ? '' : 's'}
            </span>
          </div>
        </div>
        <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
          {currentAgg ? (
            <>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8 }}>
                <span style={{ fontWeight: 800, fontSize: 15, color: C.ink }}>{fmtMoney(currentAgg.invoiced)}</span>
                <span style={{ fontSize: 12.5, color: C.muted }}>of {fmtMoney(currentAgg.target)}</span>
                <DiffBadge invoiced={currentAgg.invoiced} target={currentAgg.target} />
              </div>
              <div style={{ fontSize: 11.5, color: C.muted, marginTop: 3 }}>this week</div>
            </>
          ) : (
            <div style={{ fontSize: 12.5, color: C.muted }}>no data this week</div>
          )}
        </div>
      </div>

      {expanded && (
        <div style={{ background: '#F1F4F9', borderTop: `1px solid ${C.line}` }}>
          {weeks.map((w) => (
            <PerfWeekRow key={w.start} week={w} agg={w.teams[coord.key]} color={coord.avatarBg} />
          ))}
          {weeks.length === 0 && (
            <div style={{ padding: '20px 22px', fontSize: 13, color: C.muted, textAlign: 'center' }}>
              No weeks tracked yet.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// How old a scoreboard is allowed to get before we say so out loud. The
// card used to render a cached payload with no indication of its age,
// which is how it spent six weeks presenting September as "in progress".
const PERF_STALE_AFTER_MS = 90 * 60 * 1000;

function PerfFreshness({ perf, warming, error }) {
  const generatedAt = perf?.generatedAt ? new Date(perf.generatedAt) : null;
  const age = generatedAt ? Date.now() - generatedAt.getTime() : null;
  const stale = age != null && age > PERF_STALE_AFTER_MS;

  if (!warming && !error && !stale) return null;

  const [bg, fg, text] = error
    ? ['#FEF2F2', '#B91C1C', `Showing the last good scan — refresh failed: ${error}`]
    : warming
      ? ['#EFF6FF', '#1D4ED8', 'Building the scoreboard from Jobber — about a minute.']
      : ['#FFFBEB', '#92400E',
        `These numbers are from ${generatedAt.toLocaleString('en-US', {
          month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
        })} and haven't refreshed since.`];

  return (
    <div style={{
      padding: '8px 22px', background: bg, color: fg,
      fontSize: 12.5, fontWeight: 600, borderBottom: `1px solid ${C.line}`,
    }}>
      {text}
    </div>
  );
}

function WeeklyPerformanceCard({ perf, error, warming, coordinators }) {
  return (
    <div style={{
      background: C.card, border: `1px solid ${C.line}`, borderRadius: 12,
      boxShadow: C.shadow, overflow: 'hidden',
    }}>
      <div style={{
        padding: '16px 22px', borderBottom: `1px solid ${C.line}`, background: '#FAFBFD',
      }}>
        <h2 style={{ margin: 0, fontSize: 17, fontWeight: 800, color: C.ink, display: 'flex', alignItems: 'center', gap: 9 }}>
          <FaChartLine style={{ fontSize: 15, color: C.navy }} />
          Weekly Performance
        </h2>
        <div style={{ fontSize: 12.5, color: C.muted, marginTop: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          Each week stands on its own — invoices count toward the week the work was completed.
        </div>
      </div>

      <PerfFreshness perf={perf} warming={warming} error={error} />

      {error && !perf && (
        <div style={{ padding: '30px 24px', textAlign: 'center' }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: C.red }}>Couldn't load weekly performance</div>
          <div style={{ fontSize: 12.5, color: C.muted, marginTop: 5 }}>{error}</div>
        </div>
      )}

      {!error && !perf && (
        <div style={{ padding: '36px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 30, height: 30, borderRadius: '50%',
            border: `3px solid ${C.lineSoft}`, borderTopColor: C.navy,
            animation: 'quota-spin 0.9s linear infinite',
          }} />
          <div style={{ fontSize: 13, color: C.muted }}>Scoring weeks…</div>
        </div>
      )}

      {perf && coordinators.map((c) => (
        <PerfTeamRow key={c.id} coord={c} weeks={perf.weeks} />
      ))}
      {perf && coordinators.length === 0 && (
        <div style={{ padding: '28px 24px', textAlign: 'center', fontSize: 13, color: C.muted }}>
          No teams to show yet.
        </div>
      )}
    </div>
  );
}

// ── Team visibility legend ───────────────────────────────────────
// Page-wide checkboxes controlling which coordinator teams show in
// every section below. A coordinator can keep just their own team
// checked (saved in this browser); the boss checks them all. The
// selection also lives in the URL (?teams=…) so links reproduce it.
function TeamVisibilityLegend({ coordinators, visibleIds, onToggle }) {
  if (coordinators.length < 2) return null;
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
      background: C.card, border: `1px solid ${C.line}`, borderRadius: 12,
      boxShadow: C.shadow, padding: '10px 16px', marginBottom: 18,
    }}>
      <span style={{ fontSize: 12.5, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        Teams
      </span>
      {coordinators.map((c) => {
        const checked = !visibleIds || visibleIds.has(c.id);
        return (
          <button
            key={c.id}
            type="button"
            onClick={() => onToggle(c.id)}
            title={checked ? `Hide ${c.title} on this page` : `Show ${c.title} on this page`}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              fontFamily: sharedFont, fontSize: 13, fontWeight: 700,
              color: checked ? C.ink : C.muted,
              background: checked ? '#FAFBFD' : 'transparent',
              border: `1px solid ${checked ? C.line : C.lineSoft}`,
              borderRadius: 999, padding: '6px 14px 6px 8px', cursor: 'pointer',
              opacity: checked ? 1 : 0.65,
            }}
          >
            <span style={{
              width: 17, height: 17, borderRadius: 5, flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: checked ? c.avatarBg : '#fff',
              border: `2px solid ${checked ? c.avatarBg : '#C6CDD8'}`,
              color: '#fff',
            }}>
              {checked && <FaCheck style={{ fontSize: 9 }} />}
            </span>
            <span style={{ width: 9, height: 9, borderRadius: '50%', background: c.avatarBg, flexShrink: 0 }} />
            {c.title}
          </button>
        );
      })}
    </div>
  );
}

function StatPill({ label, value, tone }) {
  const tones = {
    green: { bg: C.greenSoft, fg: C.green },
    red: { bg: C.redSoft, fg: C.red },
    plain: { bg: C.lineSoft, fg: C.inkSoft },
  };
  const t = tones[tone] || tones.plain;
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      background: t.bg, borderRadius: 999, padding: '7px 14px',
    }}>
      <span style={{ fontSize: 12.5, color: C.muted, fontWeight: 600 }}>{label}</span>
      <span style={{ fontSize: 13.5, color: t.fg, fontWeight: 800 }}>{value}</span>
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────
export default function MaintenanceQuotaTracker() {
  // Timeframe, custom dates, and expanded teams live in the URL
  // (?tf=…&start=…&end=…&open=…) so a shared link reproduces the view.
  const [searchParams, setSearchParams] = useSearchParams();

  const tfParam = searchParams.get('tf');
  const timeframe = TIMEFRAMES.some((t) => t.key === tfParam) ? tfParam : 'today';
  const customStart = searchParams.get('start') || '';
  const customEnd = searchParams.get('end') || '';
  const expandedIds = useMemo(
    () => new Set((searchParams.get('open') || '').split(',').filter(Boolean)),
    [searchParams]
  );

  const updateParams = useCallback((mutate) => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      mutate(p);
      return p;
    }, { replace: true });
  }, [setSearchParams]);

  const setTimeframe = useCallback((key) => {
    updateParams((p) => {
      p.set('tf', key);
      if (key !== 'custom') { p.delete('start'); p.delete('end'); }
    });
  }, [updateParams]);

  const setCustomStart = useCallback((v) => {
    updateParams((p) => { v ? p.set('start', v) : p.delete('start'); });
  }, [updateParams]);

  const setCustomEnd = useCallback((v) => {
    updateParams((p) => { v ? p.set('end', v) : p.delete('end'); });
  }, [updateParams]);

  const toggleExpanded = useCallback((id) => {
    updateParams((p) => {
      const open = new Set((p.get('open') || '').split(',').filter(Boolean));
      if (open.has(id)) open.delete(id); else open.add(id);
      if (open.size) p.set('open', [...open].join(','));
      else p.delete('open');
    });
  }, [updateParams]);

  const expandedUnsentIds = useMemo(
    () => new Set((searchParams.get('uopen') || '').split(',').filter(Boolean)),
    [searchParams]
  );

  const toggleUnsentExpanded = useCallback((id) => {
    updateParams((p) => {
      const open = new Set((p.get('uopen') || '').split(',').filter(Boolean));
      if (open.has(id)) open.delete(id); else open.add(id);
      if (open.size) p.set('uopen', [...open].join(','));
      else p.delete('uopen');
    });
  }, [updateParams]);

  const [refreshing, setRefreshing] = useState(false);
  // { tech, color } while the tech invoices modal is open
  const [techModal, setTechModal] = useState(null);
  const [manageOpen, setManageOpen] = useState(false);
  const [skillsOpen, setSkillsOpen] = useState(false);
  const [unassignedUnsentOpen, setUnassignedUnsentOpen] = useState(false);
  const [teams, setTeams] = useState(() => {
    const cached = cacheGet(TEAM_CACHE_KEY);
    return cached && cached.value ? cached.value.teams : null;
  });
  const [teamError, setTeamError] = useState(null);
  // { key, data } — invoiced totals for the currently selected range
  const [progress, setProgress] = useState(null);
  const [progressError, setProgressError] = useState(null);
  // { key, data } — late / uninvoiced jobs for the same range
  const [unsent, setUnsent] = useState(null);
  const [unsentError, setUnsentError] = useState(null);
  // Weekly scoreboard — independent of the timeframe selector
  const [perf, setPerf] = useState(() => {
    const cached = cacheGet(PERF_CACHE_KEY);
    return cached && cached.value ? cached.value : null;
  });
  const [perfError, setPerfError] = useState(null);
  const [perfWarming, setPerfWarming] = useState(false);

  const range = useMemo(
    () => rangeForTimeframe(timeframe, customStart, customEnd),
    [timeframe, customStart, customEnd]
  );
  const rangeKey = range === undefined ? null : range === null ? 'alltime' : `${range.start}_${range.end}`;

  const fetchTeam = useCallback(async (fresh) => {
    setTeamError(null);
    try {
      const token = localStorage.getItem('managerToken') || '';
      const url = `${API_BASE_URL}/api/jobber/team${fresh ? '?fresh=1' : ''}`;
      const resp = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok || !body.teams) {
        throw new Error(body.error || `HTTP ${resp.status}`);
      }
      setTeams(body.teams);
      cachePut(TEAM_CACHE_KEY, { teams: body.teams });
    } catch (e) {
      setTeamError(String(e.message || e));
    }
  }, []);

  // Fetch one date-range slice of an endpoint. The server answers from a
  // shared cache and does the Jobber scan on a background thread, so the
  // two things to handle are "not built yet" (poll) and a genuine error.
  const fetchSlice = useCallback(async (endpoint, rangeArg, fresh, onWarming) => {
    const token = localStorage.getItem('managerToken') || '';
    const params = new URLSearchParams();
    if (rangeArg) {
      params.set('start', rangeArg.start);
      params.set('end', rangeArg.end);
    }
    if (fresh) params.set('fresh', '1');
    const qs = params.toString();
    const url = `${API_BASE_URL}${endpoint}${qs ? `?${qs}` : ''}`;

    // The scan behind these endpoints takes a minute or more and runs on
    // a background thread, so a cold cache answers `warming` rather than
    // blocking. Poll until it lands; the ceiling is generous because the
    // alternative is telling an operator it's broken when it isn't.
    const attempts = 40;
    let lastErr = null;
    for (let i = 0; i < attempts; i++) {
      try {
        const resp = await fetch(url, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const body = await resp.json().catch(() => ({}));
        if (body && body.reason === 'warming') {
          onWarming?.(body.estimatedSeconds || 90);
          await new Promise((r) => setTimeout(r, 4000));
          continue;
        }
        if (!resp.ok || !body.teams) {
          throw new Error(body.error || `HTTP ${resp.status}`);
        }
        return body;
      } catch (e) {
        lastErr = e;
        if (i < attempts - 1) {
          await new Promise((r) => setTimeout(r, 4000));
        }
      }
    }
    throw lastErr || new Error('Timed out waiting for Jobber');
  }, []);

  // Long ranges are pulled a month at a time (sequentially, so Jobber's
  // rate limit isn't hammered) and merged; short ranges in one request.
  const chunksFor = (rangeArg) =>
    rangeArg && spanDays(rangeArg) > CHUNK_THRESHOLD_DAYS
      ? monthChunks(rangeArg.start, rangeArg.end)
      : [rangeArg];

  const [progressNote, setProgressNote] = useState(null);

  // When a long range is chunked, "fresh" only bypasses the server cache
  // for the final (most recent) chunk — fully-past months barely change,
  // and re-scanning all of them is what used to blow the 30s limit. The
  // 10-minute server cache picks up any stragglers on earlier months.
  const fetchProgress = useCallback(async (key, rangeArg, fresh) => {
    setProgressError(null);
    setRefreshing(true);
    try {
      const chunks = chunksFor(rangeArg);
      const parts = [];
      for (let i = 0; i < chunks.length; i++) {
        const label = chunks.length > 1 ? ` — month ${i + 1} of ${chunks.length}` : '';
        if (chunks.length > 1) setProgressNote(`Reading invoices${label}…`);
        const chunkFresh = fresh && i === chunks.length - 1;
        // No pause between chunks any more: the server answers these from
        // cache and paces its own Jobber scan, so back-to-back requests
        // can't drain the rate-limit bucket the way they used to.
        parts.push(await fetchSlice(
          '/api/jobber/quota-progress', chunks[i], chunkFresh,
          () => setProgressNote(`Building invoice totals from Jobber${label} — this takes a minute the first time…`),
        ));
      }
      const body = parts.length > 1 ? mergeProgress(parts) : parts[0];
      setProgress({ key, data: body });
      cachePut(PROGRESS_CACHE_PREFIX + key, body);
    } catch (e) {
      setProgressError(String(e.message || e));
    } finally {
      setRefreshing(false);
      setProgressNote(null);
    }
  }, [fetchSlice]);

  const fetchUnsent = useCallback(async (key, rangeArg, fresh) => {
    setUnsentError(null);
    try {
      const chunks = chunksFor(rangeArg);
      const parts = [];
      for (let i = 0; i < chunks.length; i++) {
        const chunkFresh = fresh && i === chunks.length - 1;
        parts.push(await fetchSlice('/api/jobber/unsent-invoices', chunks[i], chunkFresh));
      }
      const body = parts.length > 1 ? mergeUnsent(parts) : parts[0];
      setUnsent({ key, data: body });
      cachePut(UNSENT_CACHE_PREFIX + key, body);
    } catch (e) {
      setUnsentError(String(e.message || e));
    }
  }, [fetchSlice]);

  // The weekly scan reads every invoice since tracking began — over a
  // minute of work that the server does on a background thread. A cold
  // cache answers `warming`, so poll rather than calling it an error.
  const fetchPerf = useCallback(async (fresh) => {
    setPerfError(null);
    const token = localStorage.getItem('managerToken') || '';
    const url = `${API_BASE_URL}/api/jobber/quota-performance${fresh ? '?fresh=1' : ''}`;
    for (let i = 0; i < 40; i++) {
      try {
        const resp = await fetch(url, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const body = await resp.json().catch(() => ({}));
        if (body && body.reason === 'warming') {
          setPerfWarming(true);
          await new Promise((r) => setTimeout(r, 4000));
          continue;
        }
        if (!resp.ok || !body.weeks) {
          throw new Error(body.error || `HTTP ${resp.status}`);
        }
        setPerfWarming(false);
        setPerf(body);
        cachePut(PERF_CACHE_KEY, body);
        // A revalidating payload is last scan's numbers while a new one
        // runs behind it; check back so the card lands on the new ones.
        if (body._revalidating && i < 39) {
          await new Promise((r) => setTimeout(r, 6000));
          continue;
        }
        return;
      } catch (e) {
        setPerfWarming(false);
        setPerfError(String(e.message || e));
        return;
      }
    }
    setPerfWarming(false);
  }, []);

  useEffect(() => { fetchTeam(false); }, [fetchTeam]);
  // No stagger needed any more. Both of these read a shared cache, and
  // the scans they trigger are serialised against one rate-limit budget
  // on the server — which is where that contention actually belongs.
  useEffect(() => { fetchPerf(false); }, [fetchPerf]);

  // Load invoiced totals + unsent jobs whenever the timeframe changes:
  // hydrate from cache instantly, then revalidate in the background.
  // Progress and unsent run one after the other (not in parallel) —
  // both are expensive Jobber scans, and firing them together is what
  // trips Jobber's rate limit and causes Heroku 503s.
  useEffect(() => {
    if (!rangeKey) return; // custom range not fully picked yet
    const cachedProg = cacheGet(PROGRESS_CACHE_PREFIX + rangeKey);
    if (cachedProg && cachedProg.value) setProgress({ key: rangeKey, data: cachedProg.value });
    const cachedUnsent = cacheGet(UNSENT_CACHE_PREFIX + rangeKey);
    if (cachedUnsent && cachedUnsent.value) setUnsent({ key: rangeKey, data: cachedUnsent.value });
    (async () => {
      await fetchProgress(rangeKey, range, false);
      await fetchUnsent(rangeKey, range, false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeKey, fetchProgress, fetchUnsent]);

  const handleRefresh = useCallback(() => {
    if (refreshing) return;
    (async () => {
      await fetchTeam(true);
      if (rangeKey) {
        await fetchProgress(rangeKey, range, true);
        await fetchUnsent(rangeKey, range, true);
      }
      await fetchPerf(true);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshing, fetchTeam, fetchPerf, fetchProgress, fetchUnsent, rangeKey]);

  const coordinators = useMemo(() => coordinatorsFromTeams(teams || []), [teams]);

  // ── Team visibility (legend checkboxes) ──────────────────────────
  // URL param wins; otherwise the choice saved in this browser. On
  // first visit (neither set) every team is visible. `null` = all.
  const teamsParam = searchParams.get('teams');
  useEffect(() => {
    if (teamsParam !== null) return;
    let saved = null;
    try { saved = localStorage.getItem(VISIBLE_TEAMS_LS_KEY); } catch { /* private mode */ }
    if (saved && saved !== 'all') {
      updateParams((p) => p.set('teams', saved));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const visibleIds = useMemo(() => {
    if (!teamsParam) return null;
    return new Set(teamsParam.split(',').filter(Boolean));
  }, [teamsParam]);

  const toggleTeamVisible = useCallback((id) => {
    const allIds = coordinators.map((c) => c.id);
    const cur = visibleIds ? new Set(visibleIds) : new Set(allIds);
    if (cur.has(id)) cur.delete(id); else cur.add(id);
    const isAll = allIds.every((a) => cur.has(a));
    updateParams((p) => {
      if (isAll) p.delete('teams');
      else p.set('teams', allIds.filter((a) => cur.has(a)).join(','));
    });
    try {
      localStorage.setItem(
        VISIBLE_TEAMS_LS_KEY,
        isAll ? 'all' : allIds.filter((a) => cur.has(a)).join(',')
      );
    } catch { /* private mode */ }
  }, [coordinators, visibleIds, updateParams]);

  const visibleCoordinators = useMemo(
    () => (visibleIds ? coordinators.filter((c) => visibleIds.has(c.id)) : coordinators),
    [coordinators, visibleIds]
  );

  const currentProgress = progress && progress.key === rangeKey ? progress.data : null;
  const currentUnsent = unsent && unsent.key === rangeKey ? unsent.data : null;

  const rows = useMemo(
    () => buildRows(visibleCoordinators, currentProgress, timeframe, customStart, customEnd),
    [visibleCoordinators, currentProgress, timeframe, customStart, customEnd]
  );

  const totals = useMemo(() => {
    const invoiced = rows.reduce((s, r) => s + r.invoiced, 0);
    const quota = rows.reduce((s, r) => s + r.quota, 0);
    const pct = quota > 0 ? (invoiced / quota) * 100 : 0;
    return { invoiced, quota, pct };
  }, [rows]);

  const tfLabel = (TIMEFRAMES.find((t) => t.key === timeframe) || {}).label || '';
  const rangeLabel = dateRangeLabel(timeframe, customStart, customEnd);

  return (
    <div className="internal-secondary-page internal-quota-page" style={{ padding: '24px 28px', fontFamily: sharedFont, color: C.ink, minHeight: '100vh', background: C.bg }}>
      <style>{`
        @keyframes quota-spin { to { transform: rotate(360deg); } }
        /* Unsent Invoices gets a bit more width than Weekly Performance;
           stacks to one column on narrow screens. */
        .quota-two-col { display: grid; grid-template-columns: minmax(0, 1.3fr) minmax(0, 1fr); gap: 20px; align-items: start; }
        @media (max-width: 1024px) { .quota-two-col { grid-template-columns: minmax(0, 1fr); } }
      `}</style>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 14, marginBottom: 34 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: C.ink }}>Service Division Tracker</h1>
          <p style={{ margin: '4px 0 0', fontSize: 13.5, color: C.muted }}>
            Invoices sent by each coordinator's team vs. quota — credited to the day the work was completed
          </p>
          <div style={{ marginTop: 22, fontSize: 20, fontWeight: 800, color: C.navy }}>
            {tfLabel}
            {rangeLabel && (
              <span style={{ fontWeight: 600, color: C.muted, fontSize: 17 }}>
                {' '}· {rangeLabel}
              </span>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <StatPill label="Invoiced" value={fmtMoney(totals.invoiced)} tone="green" />
          <StatPill label="Quota" value={fmtMoney(totals.quota)} tone="plain" />
          <StatPill
            label="Overall"
            value={`${totals.pct.toFixed(0)}%`}
            tone={totals.pct >= 90 ? 'green' : totals.pct >= 70 ? 'plain' : 'red'}
          />

          <TimeframeDropdown
            value={timeframe}
            onChange={setTimeframe}
            customStart={customStart}
            customEnd={customEnd}
          />

          <button
            type="button"
            onClick={handleRefresh}
            title={refreshing ? 'Refreshing…' : 'Refresh'}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: 46, height: 46,
              background: refreshing ? C.blueSoft : C.card,
              border: `1px solid ${refreshing ? '#BFDBFE' : C.line}`,
              borderRadius: 12, cursor: refreshing ? 'wait' : 'pointer',
              boxShadow: C.shadow, color: C.navy,
              transition: 'background .2s ease, border-color .2s ease',
            }}
          >
            <FaSyncAlt style={{
              fontSize: 15,
              animation: refreshing ? 'quota-spin 0.7s linear infinite' : 'none',
            }} />
          </button>

          <button
            type="button"
            onClick={() => setManageOpen(true)}
            title="Assign techs to teams, tiers, projects or vacation"
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              height: 46, padding: '0 16px', background: C.card,
              border: `1px solid ${C.line}`, borderRadius: 12, cursor: 'pointer',
              boxShadow: C.shadow, color: C.navy,
              fontFamily: sharedFont, fontSize: 14, fontWeight: 700,
            }}
          >
            Manage Team
          </button>

          <button
            type="button"
            onClick={() => setSkillsOpen(true)}
            title="Who can do fan coils, heat pumps, boilers, chillers…"
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              height: 46, padding: '0 16px', background: C.card,
              border: `1px solid ${C.line}`, borderRadius: 12, cursor: 'pointer',
              boxShadow: C.shadow, color: C.navy,
              fontFamily: sharedFont, fontSize: 14, fontWeight: 700,
            }}
          >
            Skills
          </button>
        </div>
      </div>

      {/* Custom range pickers */}
      {timeframe === 'custom' && (
        <div className="quota-custom-date-range" style={{
          display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18,
          background: C.card, border: `1px solid ${C.line}`, borderRadius: 12,
          boxShadow: C.shadow, padding: '12px 16px', width: 'fit-content',
        }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: C.muted }}>From</span>
          <input
            type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)}
            style={{ fontFamily: sharedFont, fontSize: 13.5, border: `1px solid ${C.line}`, borderRadius: 8, padding: '7px 10px', color: C.inkSoft, outline: 'none' }}
          />
          <span style={{ fontSize: 13, fontWeight: 600, color: C.muted }}>to</span>
          <input
            type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)}
            style={{ fontFamily: sharedFont, fontSize: 13.5, border: `1px solid ${C.line}`, borderRadius: 8, padding: '7px 10px', color: C.inkSoft, outline: 'none' }}
          />
          {(!customStart || !customEnd) && (
            <span style={{ fontSize: 12.5, color: C.muted }}>Pick both dates to size quotas.</span>
          )}
        </div>
      )}

      {/* Which teams to show, page-wide */}
      <TeamVisibilityLegend
        coordinators={coordinators}
        visibleIds={visibleIds}
        onToggle={toggleTeamVisible}
      />

      {/* Coordinators card */}
      <div style={{
        background: C.card, border: `1px solid ${C.line}`, borderRadius: 12,
        boxShadow: C.shadow, overflow: 'hidden',
      }}>
        {/* Legend */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 18, padding: '13px 22px',
          borderBottom: `1px solid ${C.line}`, background: '#FAFBFD',
        }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12.5, fontWeight: 600, color: C.inkSoft }}>
            <span style={{
              width: 10, height: 10, borderRadius: '50%', display: 'inline-block',
              background: `linear-gradient(135deg, ${TEAM_META.Ergi.avatarBg}, ${TEAM_META.Christina.avatarBg})`,
            }} />
            Invoiced
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12.5, fontWeight: 600, color: C.inkSoft }}>
            <span style={{ width: 10, height: 10, borderRadius: '50%', background: C.lineSoft, border: `1px solid ${C.line}`, display: 'inline-block' }} />
            Remaining to quota
          </span>
          <span style={{ marginLeft: 'auto', fontSize: 12, color: C.muted, fontStyle: 'italic' }}>
            Live from Jobber — invoices attributed by salesperson, else by assigned tech
          </span>
        </div>

        {(teamError || progressError) && !(rows.length && currentProgress) && (
          <div style={{ padding: '40px 24px', textAlign: 'center' }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: C.red }}>Couldn't load data from Jobber</div>
            <div style={{ fontSize: 13, color: C.muted, marginTop: 6 }}>{teamError || progressError}</div>
          </div>
        )}

        {!teamError && !progressError && !(rows.length && currentProgress) && (
          <div style={{ padding: '56px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
            <div style={{
              width: 38, height: 38, borderRadius: '50%',
              border: `3px solid ${C.lineSoft}`, borderTopColor: C.navy,
              animation: 'quota-spin 0.9s linear infinite',
            }} />
            <div style={{ fontSize: 13.5, color: C.muted }}>
              {rangeKey ? (progressNote || 'Pulling invoices from Jobber…') : 'Pick both dates to load invoices.'}
            </div>
          </div>
        )}

        {currentProgress && rows.map((row) => (
          <QuotaRow
            key={row.id}
            row={row}
            teamInvoices={(currentProgress.allInvoices || []).filter((i) => i.team === row.key)}
            booked={
              currentUnsent && currentUnsent.booked && currentUnsent.booked.teams[row.key]
                ? currentUnsent.booked.teams[row.key].total
                : null
            }
            bookedTechs={currentUnsent && currentUnsent.booked ? currentUnsent.booked.techs : null}
            expanded={expandedIds.has(row.id)}
            onToggle={() => toggleExpanded(row.id)}
            onOpenTechInvoices={(tech, color) => setTechModal({ tech, color })}
          />
        ))}

        {currentProgress && coordinators.length > 0 && visibleCoordinators.length === 0 && (
          <div style={{ padding: '36px 24px', textAlign: 'center', fontSize: 13.5, color: C.muted }}>
            All teams are hidden — check a team in the legend above to see its numbers.
          </div>
        )}

        {currentProgress && <UnattributedSection unassigned={currentProgress.unassigned} />}
      </div>

      {/* Section break: the quota table above is the main event; the
          supporting tables live under their own header. */}
      <div style={{ margin: '52px 0 20px' }}>
        <h2 style={{ margin: 0, fontSize: 19, fontWeight: 800, color: C.ink }}>Performance &amp; Invoicing</h2>
        <p style={{ margin: '4px 0 0', fontSize: 13, color: C.muted }}>
          What feeds the quotas above — jobs still waiting on an invoice, and each team's week-by-week history
        </p>
        <div style={{ marginTop: 16, height: 1, background: C.line }} />
      </div>

      {/* Unsent Invoices and Weekly Performance side by side; stacks
          back to one column when the screen is too narrow for two. */}
      <div className="quota-two-col">

      {/* Unsent Invoices */}
      <div style={{
        background: C.card, border: `1px solid ${C.line}`, borderRadius: 12,
        boxShadow: C.shadow, overflow: 'hidden',
      }}>
        <div style={{
          padding: '16px 22px', borderBottom: `1px solid ${C.line}`, background: '#FAFBFD',
        }}>
          <h2 style={{ margin: 0, fontSize: 17, fontWeight: 800, color: C.ink, display: 'flex', alignItems: 'center', gap: 9 }}>
            <FaFileInvoiceDollar style={{ fontSize: 15, color: C.navy }} />
            Unsent Invoices
          </h2>
          <div style={{ fontSize: 12.5, color: C.muted, marginTop: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            Finished or overdue work that hasn't been invoiced yet — click a job to open it in Jobber.
          </div>
        </div>

        {unsentError && !currentUnsent && (
          <div style={{ padding: '30px 24px', textAlign: 'center' }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: C.red }}>Couldn't load unsent invoices</div>
            <div style={{ fontSize: 12.5, color: C.muted, marginTop: 5 }}>{unsentError}</div>
          </div>
        )}

        {!unsentError && !currentUnsent && (
          <div style={{ padding: '36px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 30, height: 30, borderRadius: '50%',
              border: `3px solid ${C.lineSoft}`, borderTopColor: C.navy,
              animation: 'quota-spin 0.9s linear infinite',
            }} />
            <div style={{ fontSize: 13, color: C.muted }}>
              {rangeKey ? 'Checking for outstanding jobs…' : 'Pick both dates to load jobs.'}
            </div>
          </div>
        )}

        {currentUnsent && visibleCoordinators.map((c) => (
          <UnsentTeamRow
            key={c.id}
            name={c.name}
            teamName={c.teamLabel}
            specialty={c.specialty}
            techCount={c.techs.length}
            avatarBg={c.avatarBg}
            agg={currentUnsent.teams[c.key] || { total: 0, count: 0, jobs: [] }}
            expanded={expandedUnsentIds.has(c.id)}
            onToggle={() => toggleUnsentExpanded(c.id)}
          />
        ))}

        {currentUnsent && currentUnsent.unassigned && currentUnsent.unassigned.count > 0 && (
          <div style={{ background: '#FAFBFD' }}>
            <div
              onClick={() => setUnassignedUnsentOpen((o) => !o)}
              style={{
                padding: '11px 22px', fontSize: 12.5, color: C.muted, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 8,
              }}
            >
              <FaChevronDown style={{
                fontSize: 10, transition: 'transform .2s ease',
                transform: unassignedUnsentOpen ? 'rotate(180deg)' : 'none',
              }} />
              <span>
                {currentUnsent.unassigned.count} job{currentUnsent.unassigned.count === 1 ? '' : 's'} totalling{' '}
                <span style={{ fontWeight: 700, color: C.inkSoft }}>{fmtMoney(currentUnsent.unassigned.total)}</span>
                {' '}have no assigned tech from either team.
              </span>
            </div>
            {unassignedUnsentOpen && (
              <div style={{ borderTop: `1px solid ${C.lineSoft}` }}>
                <UnsentJobsList jobs={currentUnsent.unassigned.jobs || []} indent={40} />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Weekly performance scoreboard */}
      <WeeklyPerformanceCard
        perf={perf}
        error={perfError}
        warming={perfWarming}
        coordinators={visibleCoordinators}
      />

      </div>

      {techModal && (
        <TechInvoicesModal
          tech={techModal.tech}
          color={techModal.color}
          bookedJobs={
            (currentUnsent && currentUnsent.bookedJobs ? currentUnsent.bookedJobs : [])
              .filter((j) => (j.techs || []).includes(techModal.tech.id))
              .map((j) => ({
                ...j,
                split: (j.techs || []).length,
                share: (j.amount || 0) / Math.max(1, (j.techs || []).length),
              }))
              .sort((a, z) => String(z.endAt || '').localeCompare(String(a.endAt || '')))
          }
          onClose={() => setTechModal(null)}
        />
      )}

      {manageOpen && (
        <ManageTeamModal
          onClose={() => setManageOpen(false)}
          onChanged={handleRefresh}
        />
      )}

      {skillsOpen && (
        <ManageSkillsModal onClose={() => setSkillsOpen(false)} />
      )}
    </div>
  );
}
